import { calgaryCalendarDate } from '../../utils/calgaryTime';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faCircleCheck, faMagnifyingGlass, faXmark, faPenToSquare, faCheck, faClock, faLock } from "@fortawesome/free-solid-svg-icons";
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiEye, FiEyeOff, FiFilter, FiHelpCircle, FiInbox, FiLock, FiX } from "react-icons/fi";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, isWithinInterval, isToday } from "date-fns";
import FinancialService from "@frontend/AppSettings/services/FinancialService";
import EmployeeService from "@frontend/AppSettings/services/EmployeeService";
import MatterService from "@frontend/AppSettings/services/MatterService";
import RateService from "@frontend/AppSettings/services/RateService";
import MatterCostCodeBudgetService, { CostCodeBudgetMap } from "@frontend/AppSettings/services/MatterCostCodeBudgetService";
import BillingCategoryService from "@frontend/AppSettings/services/BillingCategoryService";
import { BillingCategory } from "@frontend/Models/Rate/RateModels";
import useActiveEmployeeId from "@frontend/AppSettings/hooks/useActiveEmployeeId";
import toastService from "@frontend/AppSettings/services/ToastService";
import StatusBadge, { StatusBadgeProps } from "@frontend/Components/StatusBadge/StatusBadge";
import { isEngineeringEnvironment, isFeatureEnabled } from "@frontend/AppSettings/environmentConfig";
import { useRuntimeConfig } from "@frontend/__settings__/runtimeConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { useDayLocks } from "@frontend/Financial/TimesheetDayLocks/useDayLocks";
import {
  useInvalidateTimeRequests,
  usePendingTimeRequests,
  useWeekTimeRequests,
} from "@frontend/Financial/TimesheetDayLocks/useTimeRequests";
import { TimeRequest } from "@frontend/Financial/TimesheetDayLocks/TimeRequestService";
import DayLockHeaderToggle from "@frontend/Financial/TimesheetDayLocks/DayLockHeaderToggle";
import AnchoredPopover from "@frontend/Financial/TimesheetDayLocks/AnchoredPopover";
import TimeRequestPopover, { TimeRequestCell } from "@frontend/Financial/TimesheetDayLocks/TimeRequestPopover";
import TimeRequestsPanel from "@frontend/Financial/TimesheetDayLocks/TimeRequestsPanel";
import LockedDaysPanel from "@frontend/Financial/TimesheetDayLocks/LockedDaysPanel";
import { DateLock, NewDateLock } from "@frontend/Financial/TimesheetDayLocks/TimesheetDayLockService";
import {
  formatRequestHours,
  isDayLockMessage,
  requestCellKey,
  serverErrorMessage,
} from "@frontend/Financial/TimesheetDayLocks/dayLockHelpers";
import { blockNumberWheel, preventNumberStepKeys } from "@frontend/utils/manualNumberInput";
import TimesheetViewPrefsService from "./TimesheetViewPrefsService";
import WeekPickerCalendar from "./WeekPickerCalendar";
import "./GeneralTimesheetView.css";

const WEEK_STARTS_ON = 1; // Monday

/**
 * Turn an HttpService error into an accurate budget message. Only a real 403 is the "admins only"
 * case; a 404/400/500/network error surfaces the server's own message instead of a misleading
 * permissions line (the previous blanket text made any failed write look like a permission problem).
 */
function budgetErrorMessage(err: any, subject: string): string {
  const status = err?.status;
  const serverMsg = err?.data?.error || err?.data?.message;
  if (status === 403) return serverMsg || `Only administrators can change the ${subject}.`;
  return serverMsg || err?.message || `Failed to update ${subject}.`;
}

// Last-resort fallback ONLY. The engineering cost-code list is data-driven from the
// billing_categories catalog (Phase 2); this constant is used solely when a tenant's catalog is
// empty so the grid never renders zero category rows. The pipeline `categories` prop (Working/Travel
// sub-rows) is a separate override and does not use this. Migration 0028 seeds these codes per tenant.
const DEFAULT_TIMESHEET_CATEGORIES = [
  { code: "100", description: "Mechanical Drawing Package Development" },
  { code: "110", description: "Mechanical Model Development" },
  { code: "200", description: "Civil-Structural Engineering & Design" },
  { code: "210", description: "Structural Model Development" },
  { code: "300", description: "Electrical Drawing Package Development" },
  { code: "400", description: "Instrumentation Base Scope" },
  { code: "500", description: "Mechanical/ Project Engineering & Design" },
  { code: "600", description: "Project administration" },
  { code: "900", description: "Field Services" },
] as const;

interface BilledTimeEntry {
  bt_id?: number;
  id?: number;
  time_recorded?: string;
  date_recorded?: string;
  date?: string;
  notes?: string;
  matterid?: string;
  matter_name?: string;
  employeeid?: string;
  employee_name?: string;
  amount?: number;
  billedrate?: number;
  costofworkrate?: number;
  feildrate?: number;
  invoiceid?: string | null;
}

interface EmployeeRaw {
  EmployeeID: string;
  FirstName?: string;
  LastName?: string;
  // Nullable since Build M (field members may carry no email/phone).
  Email?: string | null;
  Phone?: string | null;
  sept_tenantid?: string;
  permish_id?: string | number | null;
  role?: string | null;
  billedrate?: string | number | null;
  costofworkrate?: string | null;
  feildrate?: string | null;
  has_signin?: boolean;
}

interface Employee {
  employeeid: string;
  name: string;
  billedrate: number;
  rateId: string | null;
}

interface Matter {
  matterid: string;
  mattername?: string;
  clientname?: string;
  status?: string;
}

interface AllMattersMap {
  [matterid: string]: Matter;
}

interface MatterDayData {
  hours: number;
  notes: string[];
  entryIds: number[];
  invoiced: boolean;
}

interface CategoryDayData {
  hours: number;
  notes: string[];
  entryIds: number[];
  invoiced: boolean;
  billed: number;
  costofwork: number;
  fieldrate: number;
}

interface CategoryRow {
  categoryCode: string;
  categoryDescription: string;
  days: Record<string, CategoryDayData>;
  rowTotal: number;
}

interface MatterRow {
  matterId: string;
  matterName: string;
  status?: string;
  days: Record<string, MatterDayData>;
  rowTotal: number;
  categories: CategoryRow[];
}

interface DayAmounts {
  billed: number;
  costofwork: number;
  fieldrate: number;
}

interface EmployeeAmounts {
  billedTotal: number;
  costofworkTotal: number;
  fieldrateTotal: number;
}

interface EmployeeGroup {
  employeeId: string;
  employeeName: string;
  billedRate: number;
  matters: MatterRow[];
  days: Record<string, number>;
  dayAmounts: Record<string, DayAmounts>;
  rowTotal: number;
  amounts: EmployeeAmounts;
}

interface EditingCell {
  employeeId: string;
  matterId: string;
  categoryCode: string;
  dateKey: string;
}

interface PendingGeneralEdit {
  employeeId: string;
  matterId: string;
  categoryCode: string;
  dateKey: string;
  newHours: number;
  oldHours: number;
  entryIds: number[];
  billedRate: number;
}

function makePendingKey(employeeId: string, matterId: string, categoryCode: string, dateKey: string): string {
  return `${employeeId}::${matterId}::${categoryCode}::${dateKey}`;
}

function parseTimeRecordedToHours(timeRecorded: string | undefined): number {
  if (!timeRecorded) return 0;
  const s = String(timeRecorded).trim();
  if (s.includes(":")) {
    const parts = s.split(":");
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    const sec = parseInt(parts[2], 10) || 0;
    return h + m / 60 + sec / 3600;
  }
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function formatHoursToDisplay(hours: number): string {
  if (hours <= 0) return "";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  return `${h}:${m.toString().padStart(2, "0")}`;
}

function formatTotalHours(hours: number): string {
  if (hours <= 0) return "0:00";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}:${m.toString().padStart(2, "0")}`;
}

function formatTimeForApi(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:00`;
}

function getEmployeeNameFromRaw(emp: EmployeeRaw): string {
  if (emp.FirstName && emp.LastName) return `${emp.FirstName} ${emp.LastName}`;
  if (emp.FirstName) return emp.FirstName;
  return emp.EmployeeID || "Unknown";
}

function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  }
  return new Date(dateStr);
}

const LOCKED_STATUSES = ["complete", "on hold"];

function isMatterStatusLocked(status: string | undefined): boolean {
  if (!status) return false;
  const cleaned = status.toLowerCase().trim().replace(/^status:\s*/i, "");
  return LOCKED_STATUSES.includes(cleaned);
}

// Projects that are finished or parked. Broader than LOCKED_STATUSES (which gates editing) so
// legacy spellings and cancelled/archived jobs are also hidden by the default project filter.
const INACTIVE_STATUSES = ["complete", "completed", "on hold", "cancelled", "canceled", "closed", "archived"];

function isMatterStatusInactive(status: string | undefined): boolean {
  if (!status) return false;
  const cleaned = status.toLowerCase().trim().replace(/^status:\s*/i, "");
  return INACTIVE_STATUSES.includes(cleaned);
}

// Status pill beside each project name — same colours/variants as the project TopBar status badge
// (Matters/MatterDetails/Components/TopBar.tsx) so a status reads the same on both screens.
function matterStatusBadge(status: string | undefined): Pick<StatusBadgeProps, "status" | "variant" | "color"> | null {
  const label = (status || "").trim().replace(/^status:\s*/i, "");
  if (!label) return null;
  const lower = label.toLowerCase();
  if (lower.includes("complete") || lower === "closed") return { status: label, variant: "completed", color: "#28a745" };
  if (lower.includes("cancel")) return { status: label, variant: "at-risk", color: "#dc3545" };
  if (lower.includes("hold")) return { status: label, variant: "pending", color: "#ffc107" };
  if (lower.includes("progress")) return { status: label, variant: "in-review", color: "#17a2b8" };
  if (lower.includes("redline")) return { status: label, variant: "in-review", color: "#ff9800" };
  if (lower.includes("not started")) return { status: label, variant: "open", color: "#6c757d" };
  if (lower.includes("archived")) return { status: label, variant: "closed", color: "#6c757d" };
  return { status: label, variant: "open", color: "#9e9e9e" };
}

interface TimesheetCategoryDef {
  code: string;
  description: string;
}

interface GeneralTimesheetViewProps {
  weeklyBudgetCapHours?: number | null;
  /** When set, only this project/matter is shown (single-project view, e.g. the pipeline
   * "My Timesheet" sub-view). Display narrowing ONLY — the identity boundary stays on the
   * server: GET /get_all_billed_time row-scopes a permish ∉ {1,2} caller to their own rows
   * (the A35 carve-out). Never switch this view to the unscoped get_billed_time/<matter_id>. */
  restrictToMatterId?: string | null;
  /** Display name for the restricted project when it isn't in the backend matter list. */
  restrictToMatterName?: string | null;
  /** Override the cost-code categories with custom sub-rows (e.g. Working Hours / Travel). */
  categories?: ReadonlyArray<TimesheetCategoryDef>;
}

const GeneralTimesheetView: React.FC<GeneralTimesheetViewProps> = ({
  weeklyBudgetCapHours = 40,
  restrictToMatterId = null,
  restrictToMatterName = null,
  categories,
}) => {
  useRuntimeConfig();
  const isCustomCategories = !!(categories && categories.length > 0);
  const activeEmployeeId = useActiveEmployeeId();
  const [timesheetViewMode, setTimesheetViewMode] = useState<"my" | "all">("my");
  const [currentUserPermishId, setCurrentUserPermishId] = useState<string | number | null>(null);
  // Day locks: true once the caller's employee row has loaded, so a NULL permish (least privilege,
  // decision b) is told apart from "still loading" and gets the lock UI the server enforces.
  const [permishResolved, setPermishResolved] = useState(false);
  const [weekStart, setWeekStart] = useState<Date>(() =>
    startOfWeek(calgaryCalendarDate(), { weekStartsOn: WEEK_STARTS_ON })
  );
  const [billedTimeEntries, setBilledTimeEntries] = useState<BilledTimeEntry[]>([]);
  // The tenant's cost-code catalog (billing_categories). Data-driven since Phase 2; a code added in
  // the admin surface auto-appears. Not used when a custom `categories` prop drives the grid (pipeline).
  const [catalogCategories, setCatalogCategories] = useState<BillingCategory[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeMattersMap, setEmployeeMattersMap] = useState<Map<string, Matter[]>>(new Map());
  const [allMatters, setAllMatters] = useState<Matter[]>([]);
  const [rateCategoriesMap, setRateCategoriesMap] = useState<Map<string, Set<string>>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
  const [expandedMatters, setExpandedMatters] = useState<Set<string>>(new Set());
  
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<{ rowKey: string; colIndex: number } | null>(null);
  const [matterBudgets, setMatterBudgets] = useState<Map<string, number | null>>(new Map());
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [isBatchMode, setIsBatchMode] = useState(false);
  // Refined grid (plans/todo/timesheet-refined-grid.md): view-only state for the Today month
  // calendar and the legend popover. Neither touches the grid's data.
  const [isWeekPickerOpen, setIsWeekPickerOpen] = useState(false);
  const todayButtonRef = useRef<HTMLButtonElement>(null);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const legendButtonRef = useRef<HTMLButtonElement>(null);
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingGeneralEdit>>(new Map());
  // Per-cost-code budgets + server group totals, cached per matter. To keep the firm-wide
  // grid light (M3), these are loaded for the currently selected/expanded matter only — the
  // per-code bars, red cells and inline editor render under that matter. group totals are
  // server-computed (review H1), never derived from the caller-scoped billedTimeEntries.
  const [costCodeBudgetsByMatter, setCostCodeBudgetsByMatter] = useState<Map<string, CostCodeBudgetMap>>(new Map());
  const [editingCodeBudget, setEditingCodeBudget] = useState<{ matterId: string; code: string } | null>(null);
  const [codeBudgetDraft, setCodeBudgetDraft] = useState("");
  const [isSavingCodeBudget, setIsSavingCodeBudget] = useState(false);

  // Project filter: when non-empty, only the checked projects are shown. Persisted server-side
  // per user along with showAllProjects and the My/All view mode
  // (plans/todo/timesheet-project-filter-persistence.md) — the STORED set changes only on user
  // actions; the grid filters by effectiveProjectIds (stored ∩ available), computed below.
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [isProjectFilterOpen, setIsProjectFilterOpen] = useState(false);
  // When false (default), inactive and unassigned projects with no hours this week are hidden.
  // Only applies where canHideInactiveProjects is true. Persisted with the filter.
  const [showAllProjects, setShowAllProjects] = useState(false);
  const projectFilterRef = useRef<HTMLDivElement>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isProjectFilterOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (projectFilterRef.current && !projectFilterRef.current.contains(e.target as Node)) {
        setIsProjectFilterOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsProjectFilterOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isProjectFilterOpen]);

  const toggleProjectFilter = useCallback((matterId: string) => {
    prefsDirtyRef.current.filter = true;
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(matterId)) next.delete(matterId);
      else next.add(matterId);
      return next;
    });
  }, []);

  const clearProjectFilter = useCallback(() => {
    prefsDirtyRef.current.filter = true;
    setSelectedProjectIds(new Set());
    setProjectSearchQuery("");
  }, []);

  // The engineering cost-code list resolved from the catalog: active codes, plus any retired code
  // that still carries hours on a visible matter (so history keeps its row). Falls back to the
  // hardcoded defaults only when the tenant catalog is empty (grid never blanks).
  const fetchedCategoryList = useMemo<TimesheetCategoryDef[]>(() => {
    if (!catalogCategories || catalogCategories.length === 0) {
      return DEFAULT_TIMESHEET_CATEGORIES.map((c) => ({ code: c.code, description: c.description }));
    }
    const codesWithHours = new Set<string>();
    billedTimeEntries.forEach((e) => {
      const m = (e.notes || "").trim().match(/^(\d{3})/);
      if (m) codesWithHours.add(m[1]);
    });
    return catalogCategories
      .filter((c) => c.active !== false || codesWithHours.has(c.categoryid))
      .map((c) => ({ code: c.categoryid, description: c.category }));
  }, [catalogCategories, billedTimeEntries]);

  // The list the grid renders: the custom `categories` prop override (pipeline Working/Travel) when
  // present, else the data-driven catalog list.
  const categoryList = useMemo<ReadonlyArray<TimesheetCategoryDef>>(
    () => (categories && categories.length > 0 ? categories : fetchedCategoryList),
    [categories, fetchedCategoryList],
  );

  const isTimesheetOnlyUser = useMemo(() => {
    const permIdNum = typeof currentUserPermishId === 'string' 
      ? parseInt(currentUserPermishId, 10) 
      : currentUserPermishId;
    return permIdNum === 2 || permIdNum === 3;
  }, [currentUserPermishId]);

  const canEditOthersTimesheets = useMemo(() => {
    const n = typeof currentUserPermishId === 'string'
      ? parseInt(currentUserPermishId, 10)
      : currentUserPermishId;
    return n === 1;
  }, [currentUserPermishId]);

  // --- Persisted view preferences (plans/todo/timesheet-project-filter-persistence.md) ---------
  // Standard firm-wide grid only: the single-project and custom-category (pipeline) embeds stay
  // session-only so they can neither pollute nor be polluted by the saved firm-wide preferences.
  const persistPrefs = !restrictToMatterId && !isCustomCategories;
  // Dirty flags are set ONLY by the user's own handlers (filter checkbox/Clear, the eye toggle,
  // the My/All buttons). Hydration never overwrites a piece the user already touched, and a save
  // fires only once something is dirty — so the permish-2/3 forced "my" and showRequestInGrid's
  // programmatic "all" can never create a save on their own.
  const prefsDirtyRef = useRef({ filter: false, showAll: false, viewMode: false });
  const prefsRequestedRef = useRef(false);
  const [prefsHydrated, setPrefsHydrated] = useState(false);
  // A stored view mode waits here until the caller's permish is known: a stored "all" must never
  // override the forced "my" of a timesheet-only (permish 2/3) user, whichever fetch wins.
  const [pendingViewMode, setPendingViewMode] = useState<"my" | "all" | null>(null);
  const prefsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!persistPrefs || prefsRequestedRef.current) return;
    prefsRequestedRef.current = true;
    let cancelled = false;
    TimesheetViewPrefsService.getPrefs().then((prefs) => {
      if (cancelled) return;
      if (prefs) {
        if (!prefsDirtyRef.current.filter) {
          setSelectedProjectIds(new Set(prefs.selectedProjectIds));
        }
        if (!prefsDirtyRef.current.showAll) {
          setShowAllProjects(prefs.showAllProjects);
        }
        setPendingViewMode(prefs.timesheetViewMode);
      }
      // null (no prefs / failure) = session-only session; saves of later user actions still run.
      setPrefsHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, [persistPrefs]);

  useEffect(() => {
    if (pendingViewMode === null || !permishResolved) return;
    if (!isTimesheetOnlyUser && !prefsDirtyRef.current.viewMode) {
      setTimesheetViewMode(pendingViewMode);
    }
    setPendingViewMode(null);
  }, [pendingViewMode, permishResolved, isTimesheetOnlyUser]);

  // Debounced fire-and-forget save of the full snapshot ("reopen the way you left it"): a failed
  // save degrades to session-only with one warning, never a toast.
  useEffect(() => {
    if (!persistPrefs || !prefsHydrated) return;
    const dirty = prefsDirtyRef.current;
    if (!dirty.filter && !dirty.showAll && !dirty.viewMode) return;
    if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);
    const snapshot = {
      selectedProjectIds: Array.from(selectedProjectIds).sort(),
      showAllProjects,
      timesheetViewMode,
    };
    prefsSaveTimerRef.current = setTimeout(() => {
      // Consume the dirty flags at fire time: once this user-initiated save lands, a later
      // PROGRAMMATIC change (showRequestInGrid's "all", the permish forced "my") finds no flag
      // set and triggers no save — it must never be persisted as the user's choice. A change
      // arriving before the fire reschedules above with the flags (and the snapshot) still live.
      prefsDirtyRef.current = { filter: false, showAll: false, viewMode: false };
      TimesheetViewPrefsService.savePrefs(snapshot).catch((error) => {
        console.warn("Timesheet view prefs: save failed — selection stays session-only.", error);
      });
    }, 600);
    return () => {
      if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);
    };
  }, [persistPrefs, prefsHydrated, selectedProjectIds, showAllProjects, timesheetViewMode]);

  // The inactive/unassigned project filter is scoped to the standard engineering firm-wide grid:
  // not legal, not bid/pipeline tenants, and not the single-project or custom-category (pipeline)
  // embeds. It applies to every permish; timesheet-only users (2/3) never carry the firm-wide
  // matter seed, so for them it only hides their assigned inactive projects.
  const isStandardEngineeringGrid =
    isEngineeringEnvironment &&
    !isFeatureEnabled("bids") &&
    !restrictToMatterId &&
    !isCustomCategories;
  const canHideInactiveProjects =
    isStandardEngineeringGrid &&
    currentUserPermishId !== null;
  const hideInactiveProjects = canHideInactiveProjects && !showAllProjects;
  // Employee/matter pairs with unsaved batch edits. The hide filter keeps these so pending hours
  // never vanish from the grid or the totals. Keyed on a joined string so the grouping memo only
  // recomputes when the set of pairs changes, not on every keystroke-level pending edit.
  const pendingPairsKey = useMemo(() => {
    const pairs = new Set<string>();
    pendingEdits.forEach((edit) => pairs.add(`${edit.employeeId}::${edit.matterId}`));
    return Array.from(pairs).sort().join("|");
  }, [pendingEdits]);

  // Only administrators (permish_id 1) may change a project's hour budget.
  const canEditBudget = canEditOthersTimesheets;

  const canEditEmployeeTime = useCallback(
    (employeeId: string) => {
      if (!activeEmployeeId) return false;
      if (employeeId === activeEmployeeId) return true;
      return canEditOthersTimesheets;
    },
    [activeEmployeeId, canEditOthersTimesheets]
  );

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: WEEK_STARTS_ON });
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    let d = weekStart;
    while (d <= weekEnd) {
      dates.push(d);
      d = addDays(d, 1);
    }
    return dates;
  }, [weekStart, weekEnd]);

  // --- Timesheet day locks (plans/todo/timesheet-day-locks.md, F2) ---------------------------
  // Everything below is behind the `timesheetDayLocks` flag and is off for the custom-category
  // (pipeline Working/Travel) override, whose rows carry no cost code to request against. Locks
  // never apply to permish 1; permish 2/3 visuals also need at least one lock (a weekday or a
  // date — plans/todo/timesheet-date-locks.md, HD4).
  const dayLocksFeature = isFeatureEnabled("timesheetDayLocks") && !isCustomCategories;
  const dayLocks = useDayLocks(dayLocksFeature);
  const isDayLockAdmin = dayLocksFeature && canEditOthersTimesheets;
  const dayLockAppliesToMe =
    dayLocksFeature && permishResolved && !canEditOthersTimesheets && dayLocks.hasLocks;
  const weekFromKey = format(weekStart, "yyyy-MM-dd");
  const weekToKey = format(weekEnd, "yyyy-MM-dd");
  const weekRequests = useWeekTimeRequests(dayLockAppliesToMe, weekFromKey, weekToKey);
  // Employee/matter pairs with a time request this week. The hide filter keeps these too, so a
  // request's pending/declined marker never vanishes with its inactive (e.g. cancelled) project.
  const requestPairsKey = useMemo(() => {
    const pairs = new Set<string>();
    weekRequests.forEach((request) => pairs.add(`${request.employeeId}::${request.matterId}`));
    return Array.from(pairs).sort().join("|");
  }, [weekRequests]);
  const pendingRequests = usePendingTimeRequests(isDayLockAdmin);
  const invalidateTimeRequests = useInvalidateTimeRequests();
  const [requestCell, setRequestCell] = useState<(TimeRequestCell & { employeeId: string }) | null>(null);
  const [requestAnchor, setRequestAnchor] = useState<HTMLElement | null>(null);
  const [isRequestsPanelOpen, setIsRequestsPanelOpen] = useState(false);
  const [isLockedDaysPanelOpen, setIsLockedDaysPanelOpen] = useState(false);
  const [highlightRequestId, setHighlightRequestId] = useState<string | null>(null);
  const lastClickedCellRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandledRef = useRef(false);

  // Keep sticky employee row flush under the date header as thead height changes.
  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!wrapper || isLoading) return;
    const thead = wrapper.querySelector("thead");
    if (!thead) return;
    const update = () => {
      wrapper.style.setProperty(
        "--general-timesheet-sticky-head-offset",
        `${thead.getBoundingClientRect().height}px`
      );
    };
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(thead);
    return () => ro.disconnect();
  }, [isLoading, weekDates.length]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const [billedTimeRaw, employeesRaw, ratesRaw, categoriesRaw] = await Promise.all([
        FinancialService.getAllBilledTime(),
        EmployeeService.getEmployees(),
        RateService.getRates().catch(() => []),
        BillingCategoryService.getCategories().catch(() => [] as BillingCategory[]),
      ]);

      setCatalogCategories(Array.isArray(categoriesRaw) ? categoriesRaw : []);

      const rCatMap = new Map<string, Set<string>>();
      (Array.isArray(ratesRaw) ? ratesRaw : []).forEach((r: any) => {
        if (r.rate_id && r.assoc_categories) {
          const ids = String(r.assoc_categories).split(',').map((s: string) => s.trim()).filter(Boolean);
          if (ids.length > 0) rCatMap.set(r.rate_id, new Set(ids));
        }
      });
      setRateCategoriesMap(rCatMap);

      let allMattersList: Matter[] = [];
      try {
        const allMattersRaw = await MatterService.getMatters();
        allMattersList = Array.isArray(allMattersRaw) ? allMattersRaw : [];
      } catch (e) {
        console.error("Error fetching all matters:", e);
      }
      setAllMatters(allMattersList);

      const billedTimeData = Array.isArray(billedTimeRaw) 
        ? billedTimeRaw 
        : (billedTimeRaw as any)?.data ?? [];
      setBilledTimeEntries(Array.isArray(billedTimeData) ? billedTimeData : []);

      const employeeListRaw: EmployeeRaw[] = Array.isArray(employeesRaw) ? employeesRaw : [];
      const normalizedEmployees: Employee[] = employeeListRaw.map((e) => {
        const rawRate = e.billedrate;
        const isUuid = typeof rawRate === 'string' && rawRate.includes('-');
        return {
          employeeid: e.EmployeeID,
          name: getEmployeeNameFromRaw(e),
          billedrate: typeof rawRate === 'number' ? rawRate : (isUuid ? 0 : parseFloat(String(rawRate)) || 0),
          rateId: isUuid ? rawRate : null,
        };
      });
      setEmployees(normalizedEmployees);

      const mattersMap = new Map<string, Matter[]>();
      // Single-project view: the per-employee matter fan-out only decides which matters a row
      // COULD show, and the restricted grid pins that to one matter anyway — fetch assignments
      // for the caller alone rather than N employees (the A7 fetch-weight hazard).
      const fanOutEmployees = restrictToMatterId
        ? normalizedEmployees.filter((emp) => emp.employeeid === activeEmployeeId)
        : normalizedEmployees;
      await Promise.all(
        fanOutEmployees.map(async (emp) => {
          try {
            const empId = emp.employeeid;
            if (!empId) return;
            const mattersResponse = await MatterService.getMattersByEmployee(empId);
            const matters = Array.isArray(mattersResponse) 
              ? mattersResponse 
              : (mattersResponse as any)?.data ?? [];
            mattersMap.set(empId, matters);
          } catch (e) {
            console.error(`Error fetching matters for employee:`, e);
          }
        })
      );
      setEmployeeMattersMap(mattersMap);

    } catch (e) {
      console.error("Error fetching timesheet data:", e);
      setBilledTimeEntries([]);
      setEmployees([]);
      setAllMatters([]);
    } finally {
      setIsLoading(false);
    }
  }, [restrictToMatterId, activeEmployeeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchCurrentUserPermission = async () => {
      if (!activeEmployeeId) return;
      try {
        const employeeData = await EmployeeService.getEmployeeById(activeEmployeeId);
        if (employeeData) setPermishResolved(true);
        if (employeeData?.permish_id !== undefined) {
          setCurrentUserPermishId(employeeData.permish_id);
          const permIdNum = typeof employeeData.permish_id === 'string' 
            ? parseInt(employeeData.permish_id, 10) 
            : employeeData.permish_id;
          if (permIdNum === 2 || permIdNum === 3) {
            setTimesheetViewMode("my");
          }
        }
      } catch (error) {
        console.error("Error fetching current user permission:", error);
      }
    };
    fetchCurrentUserPermission();
  }, [activeEmployeeId]);

  // Single-project view: open the active user's project and its sub-rows so the
  // Working Hours / Travel breakdown is visible without an extra click.
  useEffect(() => {
    if (restrictToMatterId && activeEmployeeId) {
      setExpandedEmployees(new Set([activeEmployeeId]));
      setExpandedMatters(new Set([`${activeEmployeeId}::${restrictToMatterId}`]));
    }
  }, [restrictToMatterId, activeEmployeeId]);

  const toggleEmployee = (employeeId: string) => {
    setExpandedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  const toggleMatter = (employeeId: string, matterId: string) => {
    const key = `${employeeId}::${matterId}`;
    setExpandedMatters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const allMattersMap = useMemo(() => {
    const map: AllMattersMap = {};
    allMatters.forEach((m) => {
      map[m.matterid] = m;
    });
    return map;
  }, [allMatters]);

  const lockedMatterIds = useMemo(() => {
    const set = new Set<string>();
    allMatters.forEach((m) => {
      if (isMatterStatusLocked(m.status)) set.add(m.matterid);
    });
    return set;
  }, [allMatters]);

  const isMatterLockedForUser = useCallback(
    (matterId: string): boolean => {
      return isTimesheetOnlyUser && lockedMatterIds.has(matterId);
    },
    [isTimesheetOnlyUser, lockedMatterIds]
  );

  const { employeeGroups, dailyTotals, weekTotal } = useMemo(() => {
    const dayKeys = weekDates.map((d) => format(d, "yyyy-MM-dd"));
    const emptyDayData = (): MatterDayData => ({ hours: 0, notes: [], entryIds: [], invoiced: false });
    const emptyCategoryDayData = (): CategoryDayData => ({ hours: 0, notes: [], entryIds: [], invoiced: false, billed: 0, costofwork: 0, fieldrate: 0 });

    const extractCategoryCode = (notes: string): string => {
      if (isCustomCategories) {
        const lower = (notes || "").toLowerCase();
        const match = categoryList.find(
          (c) => lower.includes(c.description.toLowerCase()) || lower.startsWith(c.code.toLowerCase())
        );
        // Everything that isn't explicitly another category falls under the first row
        // (Working Hours in the pipeline pair). The catch-all means a cell can aggregate
        // entries whose original notes differ; editing that cell re-stamps the surviving
        // entry with the custom label (the pre-existing multi-entry collapse in
        // handleCellBlur / handleSaveAllPending).
        return match ? match.code : categoryList[0].code;
      }
      const trimmed = (notes || "").trim();
      const match = trimmed.match(/^(\d{3})/);
      return match ? match[1] : "uncategorized";
    };

    type EmpMatterCategoryData = Map<string, Map<string, Map<string, Record<string, CategoryDayData>>>>;
    const billedTimeByEmpMatterCategory: EmpMatterCategoryData = new Map();

    billedTimeEntries.forEach((entry) => {
      const dateStr = entry.date_recorded || entry.date;
      if (!dateStr) return;
      const d = parseLocalDate(dateStr);
      if (isNaN(d.getTime())) return;
      if (!isWithinInterval(d, { start: weekStart, end: weekEnd })) return;

      const key = format(d, "yyyy-MM-dd");
      const hours = parseTimeRecordedToHours(entry.time_recorded);
      const notes = (entry.notes || "").trim();
      const matterId = entry.matterid || "unknown";
      const empId = entry.employeeid || "unknown";
      const entryId = (entry as any).bt_id ?? (entry as any).btId ?? entry.id;
      const categoryCode = extractCategoryCode(notes);

      if (!billedTimeByEmpMatterCategory.has(empId)) {
        billedTimeByEmpMatterCategory.set(empId, new Map());
      }
      const empMatters = billedTimeByEmpMatterCategory.get(empId)!;

      if (!empMatters.has(matterId)) {
        empMatters.set(matterId, new Map());
      }
      const matterCategories = empMatters.get(matterId)!;

      if (!matterCategories.has(categoryCode)) {
        matterCategories.set(categoryCode, Object.fromEntries(dayKeys.map((k) => [k, emptyCategoryDayData()])));
      }
      const categoryDays = matterCategories.get(categoryCode)!;
      if (!categoryDays[key]) categoryDays[key] = emptyCategoryDayData();
      categoryDays[key].hours += hours;
      if (notes) categoryDays[key].notes.push(notes);
      const numId = entryId != null && entryId !== "" ? Number(entryId) : undefined;
      if (numId != null && !Number.isNaN(numId)) categoryDays[key].entryIds.push(numId);
      if (entry.invoiceid) categoryDays[key].invoiced = true;

      // Field Services (900) bills at the employee's field rate and is tracked
      // as the green "Field" amount. All other categories use the standard
      // billed rate and are tracked as the blue "Billed" amount.
      const isFieldServices = categoryCode === "900";
      const billedRate = parseFloat(String(entry.billedrate ?? 0)) || 0;
      const fieldRate = parseFloat(String(entry.feildrate ?? 0)) || 0;
      const cwr = parseFloat(String(entry.costofworkrate ?? 0)) || 0;
      categoryDays[key].billed += isFieldServices ? 0 : hours * billedRate;
      categoryDays[key].costofwork += hours * cwr;
      categoryDays[key].fieldrate += isFieldServices ? hours * fieldRate : 0;
    });

    const employeeGroups: EmployeeGroup[] = employees.map((emp) => {
      const empId = emp.employeeid;
      const empName = emp.name;
      const billedRate = emp.billedrate;

      const allowedCategoryIds = emp.rateId ? rateCategoriesMap.get(emp.rateId) : null;
      const empCategories = isCustomCategories
        ? categoryList
        : allowedCategoryIds && allowedCategoryIds.size > 0
          ? categoryList.filter((cat) => allowedCategoryIds.has(cat.code))
          : categoryList;

      const assignedMatters = employeeMattersMap.get(empId) || [];
      const billedMatters = billedTimeByEmpMatterCategory.get(empId) || new Map();

      const allMatterIds = new Set<string>();
      assignedMatters.forEach((m) => allMatterIds.add(m.matterid));
      billedMatters.forEach((_, matterId) => allMatterIds.add(matterId));
      // Every other employee (only visible in "All" view) shows just the matters they are
      // assigned to or have billed time on — the same set as their own "My" view. Only the
      // active user's own row is seeded with every firm matter, so they can still log time
      // to a project they aren't assigned to yet. Timesheet-only users never get the seed.
      const isActiveUserRow = empId === activeEmployeeId;
      if (isActiveUserRow && !isTimesheetOnlyUser) {
        allMatters.forEach((m) => allMatterIds.add(m.matterid));
      }

      // Single-project view: collapse to just the restricted matter. Keep it
      // present for the active user so the view never renders empty.
      if (restrictToMatterId) {
        const hasData = billedMatters.has(restrictToMatterId) ||
          assignedMatters.some((m) => m.matterid === restrictToMatterId);
        allMatterIds.clear();
        if (hasData || empId === activeEmployeeId) {
          allMatterIds.add(restrictToMatterId);
        }
      }

      // Default engineering filter: drop projects that are inactive or only present through the
      // firm-wide seed (not assigned) — but never one with time logged this week, so no hours are
      // hidden and the week totals are unchanged. Administrators (permish 1) keep every active
      // firm project on their own row, assigned or not.
      if (hideInactiveProjects) {
        const keptPairs = new Set([
          ...(pendingPairsKey ? pendingPairsKey.split("|") : []),
          ...(requestPairsKey ? requestPairsKey.split("|") : []),
        ]);
        const keepUnassigned = isActiveUserRow && canEditOthersTimesheets;
        Array.from(allMatterIds).forEach((matterId) => {
          if (billedMatters.has(matterId)) return;
          if (keptPairs.has(`${empId}::${matterId}`)) return;
          const assignedMatter = assignedMatters.find((m) => m.matterid === matterId);
          const status = allMattersMap[matterId]?.status ?? assignedMatter?.status;
          if ((!assignedMatter && !keepUnassigned) || isMatterStatusInactive(status)) allMatterIds.delete(matterId);
        });
      }

      const matterRows: MatterRow[] = Array.from(allMatterIds).map((matterId) => {
        const assignedMatter = assignedMatters.find((m) => m.matterid === matterId);
        const globalMatter = allMattersMap[matterId];
        const baseName =
          assignedMatter?.mattername ||
          globalMatter?.mattername ||
          (restrictToMatterId && matterId === restrictToMatterId ? restrictToMatterName : null) ||
          "Untitled Project";
        const matterName = `${matterId} - ${baseName}`;
        
        const matterCategoryData = billedMatters.get(matterId) || new Map();

        const categoryRows: CategoryRow[] = empCategories.map((cat) => {
          const categoryDays = matterCategoryData.get(cat.code);
          const days: Record<string, CategoryDayData> = {};
          dayKeys.forEach((k) => {
            days[k] = categoryDays?.[k] || emptyCategoryDayData();
          });
          const rowTotal = Object.values(days).reduce((a, c) => a + c.hours, 0);
          return {
            categoryCode: cat.code,
            categoryDescription: cat.description,
            days,
            rowTotal,
          };
        });

        const days: Record<string, MatterDayData> = {};
        dayKeys.forEach((k) => {
          days[k] = emptyDayData();
          categoryRows.forEach((catRow) => {
            days[k].hours += catRow.days[k].hours;
            days[k].notes.push(...catRow.days[k].notes);
            days[k].entryIds.push(...catRow.days[k].entryIds);
            if (catRow.days[k].invoiced) days[k].invoiced = true;
          });
        });
        
        const rowTotal = Object.values(days).reduce((a, c) => a + c.hours, 0);
        const status = globalMatter?.status ?? assignedMatter?.status;
        return { matterId, matterName, status, days, rowTotal, categories: categoryRows };
      });

      matterRows.sort((a, b) => a.matterName.localeCompare(b.matterName));

      const empDays: Record<string, number> = {};
      const dayAmounts: Record<string, DayAmounts> = {};
      dayKeys.forEach((k) => {
        empDays[k] = 0;
        dayAmounts[k] = { billed: 0, costofwork: 0, fieldrate: 0 };
      });
      matterRows.forEach((mr) => {
        mr.categories.forEach((cat) => {
          Object.entries(cat.days).forEach(([date, catDay]) => {
            empDays[date] += catDay.hours;
            dayAmounts[date].billed += catDay.billed;
            dayAmounts[date].costofwork += catDay.costofwork;
            dayAmounts[date].fieldrate += catDay.fieldrate;
          });
        });
      });
      const rowTotal = Object.values(empDays).reduce((a, b) => a + b, 0);
      let billedTotal = 0, costofworkTotal = 0, fieldrateTotal = 0;
      Object.values(dayAmounts).forEach((da) => {
        billedTotal += da.billed;
        costofworkTotal += da.costofwork;
        fieldrateTotal += da.fieldrate;
      });
      const amounts: EmployeeAmounts = { billedTotal, costofworkTotal, fieldrateTotal };

      return { employeeId: empId, employeeName: empName, billedRate, matters: matterRows, days: empDays, dayAmounts, rowTotal, amounts };
    });

    let filteredGroups = employeeGroups.filter((eg) => eg.matters.length > 0);

    const dailyTotals: Record<string, number> = {};
    dayKeys.forEach((k) => (dailyTotals[k] = 0));
    filteredGroups.forEach((eg) => {
      Object.entries(eg.days).forEach(([date, hours]) => {
        dailyTotals[date] += hours;
      });
    });
    const weekTotal = Object.values(dailyTotals).reduce((a, b) => a + b, 0);

    return { employeeGroups: filteredGroups, dailyTotals, weekTotal };
  }, [billedTimeEntries, employees, employeeMattersMap, allMatters, allMattersMap, weekStart, weekEnd, weekDates, rateCategoriesMap, isTimesheetOnlyUser, activeEmployeeId, restrictToMatterId, restrictToMatterName, categoryList, isCustomCategories, hideInactiveProjects, pendingPairsKey, requestPairsKey, canEditOthersTimesheets]);

  const displayedGroupsBase = useMemo(() => {
    if (timesheetViewMode === "my") {
      return employeeGroups.filter((eg) => eg.employeeId === activeEmployeeId);
    }
    return employeeGroups;
  }, [employeeGroups, timesheetViewMode, activeEmployeeId]);

  const displayedGroups = useMemo(() => {
    if (pendingEdits.size === 0) return displayedGroupsBase;
    return displayedGroupsBase.map(eg => {
      const newDays: Record<string, number> = { ...eg.days };
      const newMatters = eg.matters.map(mr => {
        const newMatterDays: Record<string, MatterDayData> = {};
        Object.entries(mr.days).forEach(([k, v]) => { newMatterDays[k] = { ...v }; });
        const newCategories = mr.categories.map(cat => {
          const newCatDays: Record<string, CategoryDayData> = {};
          Object.entries(cat.days).forEach(([k, v]) => { newCatDays[k] = { ...v }; });
          Object.keys(newCatDays).forEach(dateKey => {
            const pk = makePendingKey(eg.employeeId, mr.matterId, cat.categoryCode, dateKey);
            const pending = pendingEdits.get(pk);
            if (pending) {
              const diff = pending.newHours - newCatDays[dateKey].hours;
              newCatDays[dateKey] = { ...newCatDays[dateKey], hours: pending.newHours };
              if (newMatterDays[dateKey]) {
                newMatterDays[dateKey] = { ...newMatterDays[dateKey], hours: newMatterDays[dateKey].hours + diff };
              }
              newDays[dateKey] = (newDays[dateKey] || 0) + diff;
            }
          });
          const catTotal = Object.values(newCatDays).reduce((a, c) => a + c.hours, 0);
          return { ...cat, days: newCatDays, rowTotal: catTotal };
        });
        const matterTotal = Object.values(newMatterDays).reduce((a, c) => a + c.hours, 0);
        return { ...mr, days: newMatterDays, categories: newCategories, rowTotal: matterTotal };
      });
      const empTotal = Object.values(newDays).reduce((a, b) => a + b, 0);
      return { ...eg, days: newDays, matters: newMatters, rowTotal: empTotal };
    });
  }, [displayedGroupsBase, pendingEdits]);

  // Unique list of projects available to filter on (from the currently visible groups,
  // ignoring the active filter so the checkbox list always shows every option).
  const availableProjects = useMemo(() => {
    const map = new Map<string, string>();
    displayedGroups.forEach((eg) => {
      eg.matters.forEach((mr) => {
        if (!map.has(mr.matterId)) map.set(mr.matterId, mr.matterName);
      });
    });
    return Array.from(map.entries())
      .map(([matterId, matterName]) => ({ matterId, matterName }))
      .sort((a, b) => a.matterName.localeCompare(b.matterName));
  }, [displayedGroups]);

  const filteredAvailableProjects = useMemo(() => {
    const q = projectSearchQuery.trim().toLowerCase();
    if (!q) return availableProjects;
    return availableProjects.filter((p) => p.matterName.toLowerCase().includes(q));
  }, [availableProjects, projectSearchQuery]);

  // The selection the grid FILTERS by: stored ∩ available. The stored set is the user's saved
  // preference and is never written back from here — a project hidden by the current view (e.g.
  // visible only in All view) or deleted simply has no effect until it is available again. When
  // the whole stored selection is unavailable, the filter stays ACTIVE with zero matches (empty
  // grid + the "No selected projects…" message + the chip's Clear), never "no filter".
  const effectiveProjectIds = useMemo(() => {
    if (selectedProjectIds.size === 0) return selectedProjectIds;
    const availableIds = new Set(availableProjects.map((p) => p.matterId));
    let dropped = false;
    const next = new Set<string>();
    selectedProjectIds.forEach((id) => {
      if (availableIds.has(id)) next.add(id);
      else dropped = true;
    });
    return dropped ? next : selectedProjectIds;
  }, [selectedProjectIds, availableProjects]);

  // Apply the project filter: keep only selected matters and re-roll each employee's day
  // totals / amounts so the footer and progress bar reflect the filter. The short-circuit keys
  // on the STORED set's size on purpose (see effectiveProjectIds above).
  const filteredGroups = useMemo(() => {
    if (selectedProjectIds.size === 0) return displayedGroups;
    const dayKeys = weekDates.map((d) => format(d, "yyyy-MM-dd"));
    return displayedGroups
      .map((eg) => {
        const matters = eg.matters.filter((mr) => effectiveProjectIds.has(mr.matterId));
        const days: Record<string, number> = {};
        const dayAmounts: Record<string, DayAmounts> = {};
        dayKeys.forEach((k) => {
          days[k] = 0;
          dayAmounts[k] = { billed: 0, costofwork: 0, fieldrate: 0 };
        });
        matters.forEach((mr) => {
          mr.categories.forEach((cat) => {
            Object.entries(cat.days).forEach(([date, catDay]) => {
              days[date] += catDay.hours;
              dayAmounts[date].billed += catDay.billed;
              dayAmounts[date].costofwork += catDay.costofwork;
              dayAmounts[date].fieldrate += catDay.fieldrate;
            });
          });
        });
        const rowTotal = Object.values(days).reduce((a, b) => a + b, 0);
        let billedTotal = 0, costofworkTotal = 0, fieldrateTotal = 0;
        Object.values(dayAmounts).forEach((da) => {
          billedTotal += da.billed;
          costofworkTotal += da.costofwork;
          fieldrateTotal += da.fieldrate;
        });
        return { ...eg, matters, days, dayAmounts, rowTotal, amounts: { billedTotal, costofworkTotal, fieldrateTotal } };
      })
      // Every employee is seeded with all global matters, so filtering alone leaves
      // unrelated employees showing the selected projects at 0:00. Keep the active user
      // (so they can still log into an empty selected project) plus anyone with hours.
      .filter((eg) => eg.matters.length > 0 && (eg.employeeId === activeEmployeeId || eg.rowTotal > 0));
  }, [displayedGroups, selectedProjectIds, effectiveProjectIds, weekDates, activeEmployeeId]);

  // If the project whose progress is shown gets filtered out (unchecked, or no longer part of
  // the effective selection), clear the selection so the progress bar doesn't reference a
  // project the grid isn't showing. Gated on the STORED size: with no filter at all, every
  // project shows and nothing needs clearing.
  useEffect(() => {
    if (selectedMatterId && selectedProjectIds.size > 0 && !effectiveProjectIds.has(selectedMatterId)) {
      setSelectedMatterId(null);
    }
  }, [selectedMatterId, selectedProjectIds, effectiveProjectIds]);

  // Same for the inactive/unassigned hide filter: re-hiding must not leave the progress bar on a
  // project that is no longer in the grid.
  useEffect(() => {
    if (!selectedMatterId || !hideInactiveProjects) return;
    const stillShown = availableProjects.some((p) => p.matterId === selectedMatterId);
    if (!stillShown) setSelectedMatterId(null);
  }, [selectedMatterId, hideInactiveProjects, availableProjects]);

  // Total project hours per matter (all time) for budget checks
  const totalHoursByMatter = useMemo(() => {
    const map: Record<string, number> = {};
    billedTimeEntries.forEach((entry) => {
      const matterId = entry.matterid || "unknown";
      map[matterId] = (map[matterId] || 0) + parseTimeRecordedToHours(entry.time_recorded);
    });
    return map;
  }, [billedTimeEntries]);

  // Pending batch edits already drive the values shown in the grid. Summarize their net changes so
  // selected-project and cost-code progress can show the same projected state before Save All.
  const { pendingDeltaByMatter, pendingDeltaByMatterCode } = useMemo(() => {
    const byMatter = new Map<string, number>();
    const byMatterCode = new Map<string, number>();
    pendingEdits.forEach((edit) => {
      const delta = edit.newHours - edit.oldHours;
      byMatter.set(edit.matterId, (byMatter.get(edit.matterId) || 0) + delta);
      const matterCodeKey = `${edit.matterId}::${edit.categoryCode}`;
      byMatterCode.set(matterCodeKey, (byMatterCode.get(matterCodeKey) || 0) + delta);
    });
    return { pendingDeltaByMatter: byMatter, pendingDeltaByMatterCode: byMatterCode };
  }, [pendingEdits]);

  const getMatterBudgetHours = useCallback(async (matterId: string): Promise<number | null> => {
    const cached = matterBudgets.get(matterId);
    if (cached !== undefined) return cached;
    try {
      const matter = await MatterService.getMatterDetails(matterId);
      const raw = matter?.budget;
      if (raw != null && raw !== "") {
        const parsed = parseFloat(String(raw).trim());
        if (!isNaN(parsed) && parsed > 0) {
          setMatterBudgets((prev) => new Map(prev).set(matterId, parsed));
          return parsed;
        }
      }
      setMatterBudgets((prev) => new Map(prev).set(matterId, null));
      return null;
    } catch {
      setMatterBudgets((prev) => new Map(prev).set(matterId, null));
      return null;
    }
  }, [matterBudgets]);

  const loadCostCodeBudgets = useCallback(async (matterId: string): Promise<CostCodeBudgetMap | null> => {
    try {
      const map = await MatterCostCodeBudgetService.getBudgets(matterId);
      setCostCodeBudgetsByMatter((prev) => new Map(prev).set(matterId, map));
      return map;
    } catch {
      // Leave the matter absent from the cache: no bars rather than wrong bars (review M3).
      return null;
    }
  }, []);

  // The resulting per-code total a save would produce (group − this cell's current + new),
  // using the server group total. Only checked when the matter's per-code map is loaded and
  // this isn't the custom-category (pipeline) override; returns false (uncapped) otherwise.
  const codeResultingOverBudget = useCallback(
    (
      matterId: string,
      code: string,
      currentHours: number,
      newHours: number,
      pendingKey: string,
      loadedBudgets?: CostCodeBudgetMap | null,
    ): boolean => {
      if (isCustomCategories) return false;
      const b = (loadedBudgets ?? costCodeBudgetsByMatter.get(matterId))?.[code];
      if (!b || b.budget <= 0) return false;
      const existing = pendingEdits.get(pendingKey);
      const existingDelta = existing ? existing.newHours - existing.oldHours : 0;
      const matterCodeKey = `${matterId}::${code}`;
      const otherPendingDelta = (pendingDeltaByMatterCode.get(matterCodeKey) || 0) - existingDelta;
      return b.group + otherPendingDelta - currentHours + newHours > b.budget + 1e-9;
    },
    [costCodeBudgetsByMatter, isCustomCategories, pendingEdits, pendingDeltaByMatterCode]
  );

  const projectedCodeGroup = useCallback(
    (matterId: string, code: string): number => {
      const persisted = costCodeBudgetsByMatter.get(matterId)?.[code]?.group || 0;
      return Math.max(0, persisted + (pendingDeltaByMatterCode.get(`${matterId}::${code}`) || 0));
    },
    [costCodeBudgetsByMatter, pendingDeltaByMatterCode]
  );

  // The matter's total cap for the client walls = Σ its per-code budgets when that map is loaded and
  // positive (matching the server's effective matter budget and the progress bar), falling back to
  // the raw matters.budget only when no code budgets are known. This keeps a stale/low stored budget
  // from wrongly blocking logging to codes that still have room (the reported bug).
  const effectiveMatterBudget = useCallback(
    (matterId: string, rawBudgetHours: number | null): number | null => {
      if (!isCustomCategories) {
        const map = costCodeBudgetsByMatter.get(matterId);
        if (map) {
          const sum = Object.values(map).reduce(
            (s, b) => s + (b.budget > 0 ? b.budget : 0),
            0,
          );
          if (sum > 0) return sum;
        }
      }
      return rawBudgetHours;
    },
    [costCodeBudgetsByMatter, isCustomCategories]
  );

  // A per-cost-code block message that names the code and its cap, so it is NOT confused with the
  // project-total block (which uses the "Total amount of billing is completed" wording). A single
  // full code does not mean the whole project budget is spent.
  const codeBudgetMessage = useCallback(
    (matterId: string, code: string): string => {
      const b = costCodeBudgetsByMatter.get(matterId)?.[code];
      const cap = b && b.budget > 0 ? formatTotalHours(b.budget) : null;
      return cap
        ? `Cost code ${code} has reached its budget (${cap}). Log to a code with hours left, or raise this code's budget.`
        : `Cost code ${code} has reached its budget. Log to a code with hours left, or raise its budget.`;
    },
    [costCodeBudgetsByMatter]
  );

  // When a project/matter is selected, ensure its budget + per-code budgets are loaded.
  useEffect(() => {
    if (!selectedMatterId) return;
    getMatterBudgetHours(selectedMatterId);
    if (!isCustomCategories) loadCostCodeBudgets(selectedMatterId);
  }, [selectedMatterId, getMatterBudgetHours, loadCostCodeBudgets, isCustomCategories]);

  // Expanding through the chevron does not select the matter row. Load budgets for every expanded
  // matter as well so its visible code bars and client-side cap checks never depend on which part
  // of the row the user clicked.
  useEffect(() => {
    if (isCustomCategories) return;
    expandedMatters.forEach((key) => {
      const separator = key.indexOf("::");
      const matterId = separator >= 0 ? key.slice(separator + 2) : "";
      if (matterId && !costCodeBudgetsByMatter.has(matterId)) {
        void loadCostCodeBudgets(matterId);
      }
    });
  }, [expandedMatters, costCodeBudgetsByMatter, isCustomCategories, loadCostCodeBudgets]);

  const { displayedDailyTotals, displayedWeekTotal } = useMemo(() => {
    const dayKeys = weekDates.map((d) => format(d, "yyyy-MM-dd"));
    const dailyTotals: Record<string, number> = {};
    dayKeys.forEach((k) => (dailyTotals[k] = 0));
    filteredGroups.forEach((eg) => {
      Object.entries(eg.days).forEach(([date, hours]) => {
        dailyTotals[date] += hours;
      });
    });
    const weekTotal = Object.values(dailyTotals).reduce((a, b) => a + b, 0);
    return { displayedDailyTotals: dailyTotals, displayedWeekTotal: weekTotal };
  }, [filteredGroups, weekDates]);

  const handlePrevWeek = () => setWeekStart((d) => subWeeks(d, 1));
  const handleNextWeek = () => setWeekStart((d) => addWeeks(d, 1));
  const handleToday = () => setWeekStart(startOfWeek(calgaryCalendarDate(), { weekStartsOn: WEEK_STARTS_ON }));

  const openTimeRequest = (
    employeeId: string,
    matterId: string,
    categoryCode: string,
    dateKey: string,
    currentHours: number
  ) => {
    const matterRow = employeeGroups
      .find((eg) => eg.employeeId === employeeId)
      ?.matters.find((m) => m.matterId === matterId);
    const category = categoryList.find((c) => c.code === categoryCode);
    setRequestCell({
      employeeId,
      matterId,
      matterLabel: matterRow?.matterName ?? matterId,
      costCode: categoryCode,
      costCodeLabel: category?.description ?? "",
      dateKey,
      currentHours,
    });
    setRequestAnchor(lastClickedCellRef.current);
  };

  const closeTimeRequest = useCallback(() => {
    setRequestCell(null);
    setRequestAnchor(null);
  }, []);

  const toggleWeekdayLock = async (weekday: number, lock: boolean) => {
    const next = lock
      ? [...dayLocks.lockedWeekdays, weekday]
      : dayLocks.lockedWeekdays.filter((d) => d !== weekday);
    const result = await dayLocks.setLockedWeekdays(next);
    if (result.cancelledRequests > 0) void invalidateTimeRequests();
    return result;
  };

  // Date locks (plans/todo/timesheet-date-locks.md, F2): removing one cancels the pending requests
  // no other lock covers, so the request queries refresh when it did.
  const lockDate = (lock: NewDateLock) => dayLocks.addDateLock(lock);
  const unlockDate = async (lock: DateLock) => {
    const result = await dayLocks.deleteDateLock(lock.lockId);
    if (result.cancelledRequests > 0) void invalidateTimeRequests();
    return result;
  };

  // Jump the grid to a request's week and open its row (the admin may be in My View).
  const showRequestInGrid = useCallback((request: TimeRequest) => {
    setWeekStart(startOfWeek(parseLocalDate(request.workDate), { weekStartsOn: WEEK_STARTS_ON }));
    setTimesheetViewMode("all");
    setExpandedEmployees((prev) => new Set(prev).add(request.employeeId));
    setExpandedMatters((prev) => new Set(prev).add(`${request.employeeId}::${request.matterId}`));
  }, []);

  const handleRequestApproved = async (request: TimeRequest) => {
    await fetchData();
    if (!isCustomCategories) await loadCostCodeBudgets(request.matterId);
  };

  // Email deep link (?requests=open&request=<id>): open the panel on that request and jump the grid
  // to its week. Runs once, after the admin's pending list has loaded.
  useEffect(() => {
    if (deepLinkHandledRef.current || !isDayLockAdmin) return;
    const params = new URLSearchParams(location.search);
    if (params.get("requests") !== "open") return;
    if (pendingRequests.isLoading) return;
    deepLinkHandledRef.current = true;
    setIsRequestsPanelOpen(true);
    const id = params.get("request");
    // Consume the link so a remount (List → Timesheet) does not reopen the panel on a decided request.
    params.delete("requests");
    params.delete("request");
    const rest = params.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : "" }, { replace: true });
    if (!id) return;
    setHighlightRequestId(id);
    const target = pendingRequests.requests.find((r) => r.requestId === id);
    if (target) showRequestInGrid(target);
    else toastService.info("That request has already been decided.");
  }, [isDayLockAdmin, location.pathname, location.search, navigate, pendingRequests.isLoading, pendingRequests.requests, showRequestInGrid]);

  const handleCellClick = (
    employeeId: string,
    matterId: string,
    categoryCode: string,
    dateKey: string,
    currentHours: number,
    isInvoiced: boolean
  ) => {
    if (editingCell) return;
    if (!canEditEmployeeTime(employeeId)) return;
    if (isMatterLockedForUser(matterId)) {
      toastService.warning("This project is locked. You cannot log time to a completed or on-hold project.");
      return;
    }
    if (isInvoiced) {
      toastService.warning("This entry has been invoiced and cannot be edited.");
      return;
    }
    // Day lock (F2): a locked day never enters editing — so it can never reach pendingEdits, and
    // Save All can never send a write for it. The request popover opens instead.
    if (dayLockAppliesToMe && dayLocks.isDayLocked(dateKey)) {
      openTimeRequest(employeeId, matterId, categoryCode, dateKey, currentHours);
      return;
    }
    const pendingKey = makePendingKey(employeeId, matterId, categoryCode, dateKey);
    const hasPendingValue = pendingEdits.has(pendingKey);
    if (currentHours === 0) {
      // Per-code advisory: block opening an empty cell on a code already at/over its cap.
      const cb = costCodeBudgetsByMatter.get(matterId)?.[categoryCode];
      if (!isCustomCategories && cb && cb.budget > 0 && projectedCodeGroup(matterId, categoryCode) >= cb.budget && !hasPendingValue) {
        toastService.warning(codeBudgetMessage(matterId, categoryCode));
        return;
      }
      getMatterBudgetHours(matterId).then((budgetHours) => {
        const total = (totalHoursByMatter[matterId] || 0) + (pendingDeltaByMatter.get(matterId) || 0);
        const cap = effectiveMatterBudget(matterId, budgetHours);
        if (cap != null && cap > 0 && total >= cap && !hasPendingValue) {
          toastService.warning("Total amount of billing is completed. Cannot go over limit.");
          return;
        }
        setEditingCell({ employeeId, matterId, categoryCode, dateKey });
        setEditValue("");
      });
      return;
    }
    setEditingCell({ employeeId, matterId, categoryCode, dateKey });
    setEditValue(currentHours > 0 ? currentHours.toString() : "");
  };

  const handleCellBlur = async () => {
    if (!editingCell || isSaving) return;

    const { employeeId, matterId, categoryCode, dateKey } = editingCell;
    const newHours = parseFloat(editValue) || 0;

    const empGroup = employeeGroups.find((eg) => eg.employeeId === employeeId);
    const matterRow = empGroup?.matters.find((m) => m.matterId === matterId);
    const categoryRow = matterRow?.categories.find((c) => c.categoryCode === categoryCode);
    const currentHours = categoryRow?.days[dateKey]?.hours || 0;
    const entryIds: number[] = categoryRow?.days[dateKey]?.entryIds ?? [];
    const billedRate = empGroup?.billedRate ?? 0;
    const pendingKey = makePendingKey(employeeId, matterId, categoryCode, dateKey);
    const existingPending = pendingEdits.get(pendingKey);
    const existingPendingDelta = existingPending
      ? existingPending.newHours - existingPending.oldHours
      : 0;
    const candidateDelta = newHours - currentHours;

    const category = categoryList.find((c) => c.code === categoryCode);
    const notesValue = category ? `${category.code} - ${category.description}` : "";

    const buildUpdatePayload = (hours: number) => {
      const amountNum = hours * billedRate;
      const amount = Number.isFinite(amountNum) ? Number(amountNum.toFixed(2)) : 0;
      return {
        time_recorded: formatTimeForApi(hours),
        date_recorded: dateKey,
        notes: notesValue,
        matterid: String(matterId),
        employeeid: String(employeeId),
        amount: String(amount),
        tax_percent: "0",
      };
    };

    if (newHours <= 0 && entryIds.length === 0) {
      if (isBatchMode) {
        setPendingEdits(prev => { const next = new Map(prev); next.delete(pendingKey); return next; });
      }
      setEditingCell(null);
      setEditValue("");
      return;
    }

    if (newHours === currentHours) {
      if (isBatchMode) {
        setPendingEdits(prev => {
          const next = new Map(prev);
          next.delete(pendingKey);
          return next;
        });
      }
      setEditingCell(null);
      setEditValue("");
      return;
    }

    // Validate the projected state before accepting either an immediate or batch edit. Subtract a
    // prior draft for this same cell first, then apply its replacement, so re-editing never counts
    // twice. Reaching a cap exactly is allowed; going over is rejected.
    if (newHours > 0) {
      const loadedCodeBudgets = isCustomCategories
        ? null
        : costCodeBudgetsByMatter.get(matterId) ?? await loadCostCodeBudgets(matterId);
      const budgetHours = await getMatterBudgetHours(matterId);
      const loadedCodeBudgetSum = loadedCodeBudgets
        ? Object.values(loadedCodeBudgets).reduce(
            (sum, budget) => sum + (budget.budget > 0 ? budget.budget : 0),
            0,
          )
        : 0;
      const cap = loadedCodeBudgetSum > 0
        ? loadedCodeBudgetSum
        : effectiveMatterBudget(matterId, budgetHours);
      if (cap != null && cap > 0) {
        const persistedTotal = totalHoursByMatter[matterId] || 0;
        const pendingMatterDelta = pendingDeltaByMatter.get(matterId) || 0;
        const resultingTotal = persistedTotal + pendingMatterDelta - existingPendingDelta + candidateDelta;
        if (resultingTotal > cap + 1e-9) {
          toastService.warning("Total amount of billing is completed. Cannot go over limit.");
          setEditingCell(null);
          setEditValue("");
          return;
        }
      }
      if (codeResultingOverBudget(matterId, categoryCode, currentHours, newHours, pendingKey, loadedCodeBudgets)) {
        toastService.warning(codeBudgetMessage(matterId, categoryCode));
        setEditingCell(null);
        setEditValue("");
        return;
      }
    }

    if (isBatchMode) {
      setPendingEdits(prev => new Map(prev).set(pendingKey, {
        employeeId, matterId, categoryCode, dateKey, newHours, oldHours: currentHours, entryIds, billedRate,
      }));
      setEditingCell(null);
      setEditValue("");
      return;
    }

    setIsSaving(true);
    try {
      if (entryIds.length > 0) {
        if (newHours <= 0) {
          for (const id of entryIds) {
            await FinancialService.deleteBilledTime(String(id));
          }
          toastService.success("Time entry deleted");
        } else {
          // Delete siblings before updating the survivor so the per-code wall's sum doesn't
          // still count soon-to-be-deleted rows and wrongly refuse a reduction (M4).
          for (let i = 1; i < entryIds.length; i++) {
            await FinancialService.deleteBilledTime(String(entryIds[i]));
          }
          await FinancialService.updateBilledTime(String(entryIds[0]), buildUpdatePayload(newHours));
          toastService.success("Time entry updated");
        }
      } else {
        await FinancialService.logTime({
          time_recorded: formatTimeForApi(newHours),
          date_recorded: dateKey,
          notes: notesValue,
          employeeid: employeeId,
          matterid: matterId,
        });
        toastService.success("Time entry saved");
      }
      await fetchData();
      if (!isCustomCategories) await loadCostCodeBudgets(matterId);
    } catch (e) {
      console.error("Error saving time entry:", e);
      // F3: the server's own sentence (budget, project lock, day lock) when it sent one.
      const serverMessage = serverErrorMessage(e);
      if (isDayLockMessage(serverMessage)) dayLocks.refresh();
      toastService.error(serverMessage || "Failed to save time entry");
    } finally {
      setIsSaving(false);
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    preventNumberStepKeys(e);
    if (e.key === "Enter") {
      e.preventDefault();
      handleCellBlur();
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleSaveAllPending = async () => {
    if (pendingEdits.size === 0) return;
    const editsToSave: PendingGeneralEdit[] = [];
    pendingEdits.forEach((edit) => { editsToSave.push(edit); });
    // Apply reductions/deletes before increases so the backend's per-entry cap never
    // trips on a transient over-budget total for a batch whose NET change is within budget.
    editsToSave.sort((a, b) => (a.newHours - a.oldHours) - (b.newHours - b.oldHours));

    // Aggregate per-matter cap: no matter's net change may push it over budget.
    const netByMatter = new Map<string, number>();
    editsToSave.forEach((e) => {
      netByMatter.set(e.matterId, (netByMatter.get(e.matterId) || 0) + (e.newHours - e.oldHours));
    });
    for (const [matterId, net] of netByMatter.entries()) {
      if (net <= 0) continue; // net reduction can never exceed budget
      const budgetHours = await getMatterBudgetHours(matterId);
      const cap = effectiveMatterBudget(matterId, budgetHours);
      if (cap != null && cap > 0) {
        const total = totalHoursByMatter[matterId] || 0;
        if (total + net > cap + 1e-9) {
          toastService.warning("Total amount of billing is completed. Cannot go over limit.");
          return;
        }
      }
    }

    // Per-(matter, code) aggregate advisory: no code's group total + its batch net change may
    // exceed that code's budget (only for matters whose per-code map is loaded).
    if (!isCustomCategories) {
      const netByMatterCode = new Map<string, number>();
      editsToSave.forEach((e) => {
        const k = `${e.matterId}::${e.categoryCode}`;
        netByMatterCode.set(k, (netByMatterCode.get(k) || 0) + (e.newHours - e.oldHours));
      });
      for (const [k, net] of netByMatterCode.entries()) {
        if (net <= 0) continue;
        const [mId, code] = k.split("::");
        const b = costCodeBudgetsByMatter.get(mId)?.[code];
        if (b && b.budget > 0 && b.group + net > b.budget + 1e-9) {
          toastService.warning(codeBudgetMessage(mId, code));
          return;
        }
      }
    }

    setIsSaving(true);
    let successCount = 0;
    let failCount = 0;
    let firstFailure: string | null = null;
    try {
      for (let idx = 0; idx < editsToSave.length; idx++) {
        const { employeeId, matterId, categoryCode, dateKey, newHours, entryIds, billedRate } = editsToSave[idx];
        const category = categoryList.find((c) => c.code === categoryCode);
        const notesValue = category ? `${category.code} - ${category.description}` : "";
        const buildPayload = (hours: number) => {
          const amountNum = hours * billedRate;
          const amount = Number.isFinite(amountNum) ? Number(amountNum.toFixed(2)) : 0;
          return {
            time_recorded: formatTimeForApi(hours),
            date_recorded: dateKey,
            notes: notesValue,
            matterid: String(matterId),
            employeeid: String(employeeId),
            amount: String(amount),
            tax_percent: "0",
          };
        };
        try {
          if (entryIds.length > 0) {
            if (newHours <= 0) {
              for (const id of entryIds) {
                await FinancialService.deleteBilledTime(String(id));
              }
            } else {
              // Delete siblings before updating the survivor so the per-code wall's sum doesn't
              // still count soon-to-be-deleted rows and wrongly refuse a reduction (M4).
              for (let i = 1; i < entryIds.length; i++) {
                await FinancialService.deleteBilledTime(String(entryIds[i]));
              }
              await FinancialService.updateBilledTime(String(entryIds[0]), buildPayload(newHours));
            }
          } else {
            await FinancialService.logTime({
              time_recorded: formatTimeForApi(newHours),
              date_recorded: dateKey,
              notes: notesValue,
              employeeid: employeeId,
              matterid: matterId,
            });
          }
          successCount++;
        } catch (e) {
          console.error("Error saving pending edit:", e);
          failCount++;
          if (firstFailure === null) firstFailure = serverErrorMessage(e);
        }
      }
      if (failCount === 0) {
        toastService.success(`Saved ${successCount} time ${successCount === 1 ? "entry" : "entries"}`);
      } else {
        // F3: the first failure's server sentence after the count; behaviour otherwise unchanged.
        if (isDayLockMessage(firstFailure)) dayLocks.refresh();
        toastService.warning(
          firstFailure
            ? `Saved ${successCount}, failed ${failCount}: ${firstFailure}`
            : `Saved ${successCount}, failed ${failCount}`
        );
      }
      setPendingEdits(new Map());
      setIsBatchMode(false);
      await fetchData();
      if (!isCustomCategories) {
        await Promise.all(Array.from(netByMatter.keys()).map((mId) => loadCostCodeBudgets(mId)));
      }
    } catch (e) {
      console.error("Error in batch save:", e);
      toastService.error("Failed to save time entries");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardPending = () => {
    setPendingEdits(new Map());
    setIsBatchMode(false);
  };

  const startEditingCodeBudget = (matterId: string, code: string) => {
    const b = costCodeBudgetsByMatter.get(matterId)?.[code];
    setCodeBudgetDraft(b && b.budget > 0 ? String(b.budget) : "");
    setEditingCodeBudget({ matterId, code });
  };

  const handleSaveCodeBudget = async (matterId: string, code: string) => {
    if (isSavingCodeBudget) return;
    const trimmed = codeBudgetDraft.trim();
    const parsed = trimmed === "" ? 0 : parseFloat(trimmed);
    if (trimmed !== "" && (isNaN(parsed) || parsed < 0)) {
      toastService.warning("Enter a valid budget in hours (0 or more).");
      return;
    }
    setIsSavingCodeBudget(true);
    try {
      await MatterCostCodeBudgetService.saveBudgets(matterId, [{ cost_code: code, budget_hours: parsed }]);
      // Apply locally and re-derive the overall budget (matters.budget = Σ code budgets, which the
      // server just synced) so the total bar + the matter-total guard track it this session.
      const merged: CostCodeBudgetMap = { ...(costCodeBudgetsByMatter.get(matterId) || {}) };
      merged[code] = { budget: parsed, group: merged[code]?.group || 0 };
      const newSum = Object.values(merged).reduce((s, b) => s + (b.budget || 0), 0);
      setCostCodeBudgetsByMatter((prev) => new Map(prev).set(matterId, merged));
      setMatterBudgets((prev) => new Map(prev).set(matterId, newSum > 0 ? newSum : null));
      setEditingCodeBudget(null);
      toastService.success("Cost code budget updated");
    } catch (e) {
      console.error("Error updating cost code budget:", e);
      toastService.error(budgetErrorMessage(e, "cost code budget"));
    } finally {
      setIsSavingCodeBudget(false);
    }
  };

  const handleCodeBudgetKeyDown = (e: React.KeyboardEvent, matterId: string, code: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveCodeBudget(matterId, code);
    } else if (e.key === "Escape") {
      setEditingCodeBudget(null);
    }
  };

  // Σ of the selected matter's per-code budgets. The overall project budget IS this sum
  // (matters.budget is derived and kept in sync server-side). Only the selected matter's code map
  // is loaded (M3), so this is scoped to the selected project.
  const selectedCodeBudgetSum = selectedMatterId && !isCustomCategories
    ? Object.values(costCodeBudgetsByMatter.get(selectedMatterId) ?? {}).reduce(
        (s, b) => s + (b.budget || 0),
        0,
      )
    : 0;

  // Effective budget and hours for the progress bar. When a project is selected, show the
  // project's TOTAL hours (all time) against its budget — matching the matter-scoped
  // BillingTimesheetView "Total project progress" (and the same all-time total that gates
  // logging). The budget is the sum of the code budgets; a matter with no per-code budgets yet
  // falls back to its legacy matters.budget. Otherwise fall back to the firm-wide weekly total.
  const selectedMatterBudget = selectedMatterId ? (matterBudgets.get(selectedMatterId) ?? null) : null;
  const effectiveBudgetHours = selectedMatterId
    ? (selectedCodeBudgetSum > 0 ? selectedCodeBudgetSum : selectedMatterBudget)
    : (weeklyBudgetCapHours ?? null);
  const effectiveHours = selectedMatterId
    ? Math.max(0, (totalHoursByMatter[selectedMatterId] || 0) + (pendingDeltaByMatter.get(selectedMatterId) || 0))
    : displayedWeekTotal;
  const relevantPendingCount = selectedMatterId
    ? Array.from(pendingEdits.values()).filter((edit) => edit.matterId === selectedMatterId).length
    : pendingEdits.size;

  // Percentage is uncapped so an over-budget project reads its true value (e.g. 150%),
  // matching BillingTimesheetView. The bar fill itself is capped at 100% in the JSX below.
  const budgetPercent =
    effectiveBudgetHours != null && effectiveBudgetHours > 0
      ? Math.round((effectiveHours / effectiveBudgetHours) * 100)
      : 0;
  const isOverBudget = effectiveBudgetHours != null && effectiveBudgetHours > 0
    ? effectiveHours > effectiveBudgetHours
    : false;

  const handlePickWeekDate = (date: Date) => {
    setWeekStart(startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON }));
    setIsWeekPickerOpen(false);
    todayButtonRef.current?.focus();
  };
  const closeWeekPicker = useCallback(() => setIsWeekPickerOpen(false), []);
  const escapeWeekPicker = useCallback(() => {
    setIsWeekPickerOpen(false);
    todayButtonRef.current?.focus();
  }, []);
  const closeLegend = useCallback(() => setIsLegendOpen(false), []);

  return (
    <div className="general-timesheet-container">
      <div className="general-timesheet-toolbar">
        <div className="general-timesheet-toolbar-left">
          <div className="general-timesheet-week-nav">
            <div className="general-timesheet-week-stepper">
              <button
                type="button"
                className="general-timesheet-nav-btn"
                onClick={handlePrevWeek}
                aria-label="Previous week"
              >
                <FiChevronLeft size={16} aria-hidden="true" />
              </button>
              <span className="general-timesheet-week-range">
                {format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}
              </span>
              <button
                type="button"
                className="general-timesheet-nav-btn"
                onClick={handleNextWeek}
                aria-label="Next week"
              >
                <FiChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              ref={todayButtonRef}
              className="general-timesheet-today-btn"
              onClick={() => { handleToday(); setIsWeekPickerOpen(true); }}
              aria-haspopup="dialog"
              aria-expanded={isWeekPickerOpen}
            >
              Today
            </button>
          </div>
          {!isTimesheetOnlyUser && (
            <div className="general-timesheet-view-toggle">
              <button
                type="button"
                className={`general-timesheet-view-btn ${timesheetViewMode === "my" ? "active" : ""}`}
                aria-pressed={timesheetViewMode === "my"}
                onClick={() => { prefsDirtyRef.current.viewMode = true; setTimesheetViewMode("my"); }}
              >
                <span>My View</span>
              </button>
              <button
                type="button"
                className={`general-timesheet-view-btn ${timesheetViewMode === "all" ? "active" : ""}`}
                aria-pressed={timesheetViewMode === "all"}
                onClick={() => { prefsDirtyRef.current.viewMode = true; setTimesheetViewMode("all"); }}
              >
                <span>All View</span>
              </button>
            </div>
          )}
          <div className="general-timesheet-project-filter" ref={projectFilterRef}>
            <button
              type="button"
              className={`general-timesheet-filter-btn ${selectedProjectIds.size > 0 ? "active" : ""}`}
              onClick={() => setIsProjectFilterOpen((o) => !o)}
              aria-expanded={isProjectFilterOpen}
              aria-haspopup="true"
            >
              <FiFilter size={15} aria-hidden="true" />
              <span>
                {selectedProjectIds.size > 0
                  ? `${selectedProjectIds.size} project${selectedProjectIds.size === 1 ? "" : "s"}`
                  : "Filter projects"}
              </span>
              {isProjectFilterOpen
                ? <FiChevronUp size={14} aria-hidden="true" className="general-timesheet-filter-caret" />
                : <FiChevronDown size={14} aria-hidden="true" className="general-timesheet-filter-caret" />}
            </button>
            {selectedProjectIds.size > 0 && (
              <button
                type="button"
                className="general-timesheet-filter-clear-btn"
                onClick={clearProjectFilter}
                aria-label="Clear project filter"
                title="Clear project filter"
              >
                <FiX size={15} aria-hidden="true" />
              </button>
            )}
            {isProjectFilterOpen && (
              <div className="general-timesheet-filter-panel">
                <div className="general-timesheet-filter-search">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="general-timesheet-filter-search-icon" />
                  <input
                    type="text"
                    className="general-timesheet-filter-search-input"
                    placeholder="Search projects..."
                    aria-label="Search projects"
                    value={projectSearchQuery}
                    onChange={(e) => setProjectSearchQuery(e.target.value)}
                    autoFocus
                  />
                  {projectSearchQuery && (
                    <button
                      type="button"
                      className="general-timesheet-filter-search-clear"
                      onClick={() => setProjectSearchQuery("")}
                      aria-label="Clear search"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
                <div className="general-timesheet-filter-list">
                  {filteredAvailableProjects.length === 0 ? (
                    <div className="general-timesheet-filter-empty">No projects found</div>
                  ) : (
                    filteredAvailableProjects.map((p) => (
                      <label key={p.matterId} className="general-timesheet-filter-option">
                        <input
                          type="checkbox"
                          checked={selectedProjectIds.has(p.matterId)}
                          onChange={() => toggleProjectFilter(p.matterId)}
                        />
                        <span className="general-timesheet-filter-option-name" title={p.matterName}>
                          {p.matterName}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {selectedProjectIds.size > 0 && (
                  <div className="general-timesheet-filter-footer">
                    <span>{selectedProjectIds.size} selected</span>
                    <button type="button" className="general-timesheet-filter-clear-link" onClick={clearProjectFilter}>
                      Clear all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {canHideInactiveProjects && (
            <button
              type="button"
              className={`general-timesheet-icon-btn general-timesheet-show-all-toggle ${showAllProjects ? "active" : ""}`}
              onClick={() => { prefsDirtyRef.current.showAll = true; setShowAllProjects((v) => !v); }}
              aria-pressed={showAllProjects}
              aria-label={showAllProjects ? "Showing all projects" : "Show all projects"}
              title={canEditOthersTimesheets
                ? "Completed, on-hold and other inactive projects without hours this week are hidden by default"
                : "Completed, on-hold and unassigned projects without hours this week are hidden by default"}
            >
              {showAllProjects ? <FiEye size={16} aria-hidden="true" /> : <FiEyeOff size={16} aria-hidden="true" />}
            </button>
          )}
        </div>
        <div className="general-timesheet-toolbar-right">
          <button
            type="button"
            ref={legendButtonRef}
            className="general-timesheet-icon-btn"
            onClick={() => setIsLegendOpen((o) => !o)}
            aria-label="Legend"
            aria-haspopup="dialog"
            aria-expanded={isLegendOpen}
          >
            <FiHelpCircle size={17} aria-hidden="true" />
          </button>
          {isDayLockAdmin && (
            <button
              type="button"
              className="tdl-requests-btn"
              onClick={() => setIsRequestsPanelOpen(true)}
              aria-label={`Requests (${pendingRequests.requests.length})`}
            >
              <FiInbox size={15} aria-hidden="true" />
              <span>Requests</span>
              {pendingRequests.requests.length > 0 && (
                <span className="tdl-requests-badge">{pendingRequests.requests.length}</span>
              )}
            </button>
          )}
          {isDayLockAdmin && (
            <button
              type="button"
              className="tdl-requests-btn"
              onClick={() => setIsLockedDaysPanelOpen(true)}
              disabled={!dayLocks.isReady}
            >
              <FiLock size={15} aria-hidden="true" />
              <span>Locked days</span>
            </button>
          )}
          <div className="general-timesheet-save-switch" role="group" aria-label="Save">
            <span className="general-timesheet-save-switch-label" aria-hidden="true">Save</span>
            <div className="general-timesheet-segmented">
              <button
                type="button"
                className={`general-timesheet-segment ${!isBatchMode ? "active" : ""}`}
                aria-pressed={!isBatchMode}
                onClick={() => { if (isBatchMode) handleDiscardPending(); }}
                disabled={isBatchMode && (pendingEdits.size > 0 || editingCell !== null || isSaving)}
                title={isBatchMode && (pendingEdits.size > 0 || editingCell !== null || isSaving) ? "Save All or Discard first" : undefined}
              >
                Instantly
              </button>
              <button
                type="button"
                className={`general-timesheet-segment ${isBatchMode ? "active" : ""}`}
                aria-pressed={isBatchMode}
                onClick={() => setIsBatchMode(true)}
              >
                In batch
              </button>
            </div>
            {isBatchMode && (
              <div className="general-timesheet-batch-actions" role="group" aria-label="Batch edits">
                {/* The count is announced, not shown: Save All sits beside the switch on its own. */}
                <span className="general-timesheet-pending-count ts-sr-only" role="status">
                  {pendingEdits.size} pending {pendingEdits.size === 1 ? "edit" : "edits"}
                </span>
                <button
                  type="button"
                  className="general-timesheet-discard-btn"
                  onClick={handleDiscardPending}
                  disabled={isSaving}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="general-timesheet-save-all-btn"
                  onClick={handleSaveAllPending}
                  disabled={pendingEdits.size === 0 || isSaving}
                >
                  {isSaving ? "Saving..." : "Save All"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <AnchoredPopover
        anchor={legendButtonRef.current}
        open={isLegendOpen}
        onClose={closeLegend}
        width={300}
        ariaLabel="Legend"
        className="general-timesheet-legend-popover"
      >
        <ul className="general-timesheet-legend-list">
          <li>
            <span className="general-timesheet-legend-swatch is-invoiced" aria-hidden="true">
              <FontAwesomeIcon icon={faCircleCheck} className="general-timesheet-legend-check" />
            </span>
            This check mark means the item has already been invoiced
          </li>
          <li><span className="general-timesheet-legend-swatch is-pending" aria-hidden="true" />Unsaved batch change</li>
          <li><span className="general-timesheet-legend-swatch is-over" aria-hidden="true" />Cost code at or over budget</li>
          <li>
            <span className="general-timesheet-legend-swatch is-day-locked" aria-hidden="true">
              <FontAwesomeIcon icon={faLock} />
            </span>
            Day locked
          </li>
          <li>
            <span className="general-timesheet-legend-swatch is-request-pending" aria-hidden="true">
              <FontAwesomeIcon icon={faClock} />
            </span>
            Time request pending
          </li>
          <li><span className="general-timesheet-legend-swatch is-request-declined" aria-hidden="true">✕</span>Time request declined</li>
        </ul>
      </AnchoredPopover>
      <WeekPickerCalendar
        anchor={todayButtonRef.current}
        open={isWeekPickerOpen}
        onClose={closeWeekPicker}
        onEscape={escapeWeekPicker}
        today={calgaryCalendarDate()}
        shownWeekStart={weekStart}
        onPickDate={handlePickWeekDate}
      />

      {((effectiveBudgetHours != null && effectiveBudgetHours > 0) ||
        (!isTimesheetOnlyUser && !isLoading && filteredGroups.length > 0)) && (
        <div className="general-timesheet-strip">
          {(effectiveBudgetHours != null && effectiveBudgetHours > 0) && (
            <div className="general-timesheet-progress">
              <span className="general-timesheet-progress-label">
                {selectedMatterId ? "Total project progress" : "Weekly Progress"}
              </span>
              <span className={`general-timesheet-progress-value ${isOverBudget ? 'over-budget' : ''}`}>
                {formatTotalHours(effectiveHours)} /{" "}
                {formatTotalHours(effectiveBudgetHours)}
                <span className="general-timesheet-progress-percent">
                  {budgetPercent}%
                </span>
                {relevantPendingCount > 0 && (
                  <span className="general-timesheet-progress-unsaved">Includes unsaved changes</span>
                )}
              </span>
              <div className="general-timesheet-progress-track">
                <div
                  className={`general-timesheet-progress-fill ${isOverBudget ? 'over-budget' : ''}`}
                  style={{ width: `${Math.min(100, budgetPercent)}%` }}
                />
              </div>
              {!isLoading && filteredGroups.length > 0 && (
                <span className="general-timesheet-budget-cap">
                  Budget: {formatTotalHours(effectiveBudgetHours)}
                  {selectedMatterId && (
                    <button
                      type="button"
                      className="general-timesheet-clear-project"
                      onClick={(e) => { e.stopPropagation(); setSelectedMatterId(null); }}
                      aria-label="Show all projects"
                    >
                      Show all
                    </button>
                  )}
                </span>
              )}
            </div>
          )}
          {!isTimesheetOnlyUser && !isLoading && filteredGroups.length > 0 && (
            <div className="general-timesheet-strip-amounts">
              <span className="general-timesheet-amount-tag general-timesheet-amount-billed">
                Billed: ${filteredGroups.reduce((s, r) => s + r.amounts.billedTotal, 0).toFixed(2)}
              </span>
              <span className="general-timesheet-amount-tag general-timesheet-amount-costofwork">
                CoW: ${filteredGroups.reduce((s, r) => s + r.amounts.costofworkTotal, 0).toFixed(2)}
              </span>
              <span className="general-timesheet-amount-tag general-timesheet-amount-fieldrate">
                Field: ${filteredGroups.reduce((s, r) => s + r.amounts.fieldrateTotal, 0).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="general-timesheet-grid-wrapper" ref={gridWrapperRef}>
        {isLoading ? (
          <p className="general-timesheet-loading">Loading timesheet...</p>
        ) : (
          <table className="general-timesheet-table">
            <thead>
              <tr>
                <th className="general-timesheet-col-employee">Employee / Project</th>
                {weekDates.map((d, colIndex) => (
                  <th
                    key={d.toISOString()}
                    className={`general-timesheet-col-day ${isToday(d) ? 'general-timesheet-today' : ''} ${hoveredCell?.colIndex === colIndex ? 'col-highlighted' : ''} ${(isDayLockAdmin || dayLockAppliesToMe) && dayLocks.isDayLocked(format(d, "yyyy-MM-dd")) ? 'tdl-header-locked' : ''}`}
                  >
                    <div className="general-timesheet-day-header">
                      <span className="general-timesheet-day-name">{format(d, "EEE")}</span>
                      <span className={`general-timesheet-day-num ${isToday(d) ? 'is-today' : ''}`}>{format(d, "d")}</span>
                    </div>
                    {isDayLockAdmin && (
                      <DayLockHeaderToggle
                        weekday={d.getDay()}
                        locked={dayLocks.isWeekdayLocked(d.getDay())}
                        disabled={!dayLocks.isReady}
                        onChange={(lock) => toggleWeekdayLock(d.getDay(), lock)}
                        dateKey={format(d, "yyyy-MM-dd")}
                        dateLocks={dayLocks.dateLocksFor(format(d, "yyyy-MM-dd"))}
                        onUnlockDate={unlockDate}
                        variant="chip"
                      />
                    )}
                    {dayLockAppliesToMe && dayLocks.isDayLocked(format(d, "yyyy-MM-dd")) && (
                      <span className="tdl-header-chip">
                        <FontAwesomeIcon
                          icon={faLock}
                          className="tdl-header-lock-icon"
                          title={dayLocks.lockLabelFor(format(d, "yyyy-MM-dd")) ?? "Day locked"}
                        />
                        Day locked
                      </span>
                    )}
                  </th>
                ))}
                <th className="general-timesheet-col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.length === 0 ? (
                <tr>
                  <td colSpan={weekDates.length + 2} className="general-timesheet-empty">
                    {selectedProjectIds.size > 0
                      ? "No selected projects have activity in this view."
                      : timesheetViewMode === "my"
                      ? "No time entries found. Add time entries to get started."
                      : "No employees with assigned projects found."}
                  </td>
                </tr>
              ) : (
                filteredGroups.map((emp) => {
                  const isExpanded = expandedEmployees.has(emp.employeeId);
                  const isMe = emp.employeeId === activeEmployeeId;
                  return (
                    <React.Fragment key={emp.employeeId}>
                      <tr
                        className={`general-timesheet-employee-row ${isMe ? 'general-timesheet-row-me' : ''} ${
                          isMe && isExpanded ? 'general-timesheet-employee-row--sticky' : ''
                        }`}
                        onClick={() => toggleEmployee(emp.employeeId)}
                      >
                        <td className="general-timesheet-col-employee">
                          <div className="general-timesheet-employee-cell">
                            <button
                              type="button"
                              className="general-timesheet-expand-btn"
                              aria-label={isExpanded ? "Collapse" : "Expand"}
                            >
                              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                            </button>
                            <span className="general-timesheet-avatar">{emp.employeeName.charAt(0).toUpperCase()}</span>
                            <div className="general-timesheet-employee-info">
                              <div className="general-timesheet-employee-name-line">
                                <span className="general-timesheet-employee-name">{emp.employeeName}</span>
                                {isMe && <span className="general-timesheet-you-tag">You</span>}
                              </div>
                              <span className="general-timesheet-matter-count">
                                {emp.matters.length} project{emp.matters.length !== 1 ? "s" : ""} · {formatTotalHours(emp.rowTotal)} this week
                              </span>
                              {!isTimesheetOnlyUser && (
                              <div className="general-timesheet-emp-amounts">
                                <span className="general-timesheet-amount-tag general-timesheet-amount-billed" title="Billed Amount">
                                  Billed: ${emp.amounts.billedTotal.toFixed(2)}
                                </span>
                                <span className="general-timesheet-amount-tag general-timesheet-amount-costofwork" title="Cost of Work">
                                  CoW: ${emp.amounts.costofworkTotal.toFixed(2)}
                                </span>
                                <span className="general-timesheet-amount-tag general-timesheet-amount-fieldrate" title="Field Rate">
                                  Field: ${emp.amounts.fieldrateTotal.toFixed(2)}
                                </span>
                              </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {weekDates.map((d) => {
                          const key = format(d, "yyyy-MM-dd");
                          const hours = emp.days[key] || 0;
                          const da = emp.dayAmounts[key];
                          const hasAmounts = da && (da.billed > 0 || da.costofwork > 0 || da.fieldrate > 0);
                          return (
                            <td key={key} className={`general-timesheet-col-day general-timesheet-cell ${isToday(d) ? 'general-timesheet-today-col' : ''}`}>
                              <span className={hours > 0 ? "general-timesheet-cell-hours" : "general-timesheet-cell-empty"}>
                                {hours > 0 ? formatHoursToDisplay(hours) : ""}
                              </span>
                              {!isTimesheetOnlyUser && hasAmounts && (
                                <div className="general-timesheet-cell-amounts">
                                  {da.billed > 0 && (
                                    <span className="general-timesheet-cell-amt general-timesheet-amt-billed" title="Billed">${da.billed.toFixed(0)}</span>
                                  )}
                                  {da.costofwork > 0 && (
                                    <span className="general-timesheet-cell-amt general-timesheet-amt-costofwork" title="Cost of Work">${da.costofwork.toFixed(0)}</span>
                                  )}
                                  {da.fieldrate > 0 && (
                                    <span className="general-timesheet-cell-amt general-timesheet-amt-fieldrate" title="Field Rate">${da.fieldrate.toFixed(0)}</span>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="general-timesheet-col-total general-timesheet-row-total">
                          {formatTotalHours(emp.rowTotal)}
                        </td>
                      </tr>
                      {isExpanded &&
                        emp.matters.map((matter) => {
                          const matterKey = `${emp.employeeId}::${matter.matterId}`;
                          const isMatterExpanded = expandedMatters.has(matterKey);
                          return (
                            <React.Fragment key={`${emp.employeeId}-${matter.matterId}`}>
                              <tr
                                className={`general-timesheet-matter-row ${selectedMatterId === matter.matterId ? 'general-timesheet-matter-row-selected' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedMatterId(matter.matterId);
                                  const matterKey = `${emp.employeeId}::${matter.matterId}`;
                                  setExpandedMatters((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(matterKey)) next.delete(matterKey);
                                    else next.add(matterKey);
                                    return next;
                                  });
                                }}
                              >
                                <td className="general-timesheet-col-employee general-timesheet-matter-cell">
                                  <div className="general-timesheet-matter-cell-content">
                                    <button
                                      type="button"
                                      className="general-timesheet-expand-btn general-timesheet-matter-expand-btn"
                                      aria-label={isMatterExpanded ? "Collapse categories" : "Expand categories"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleMatter(emp.employeeId, matter.matterId);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={isMatterExpanded ? faChevronUp : faChevronDown} />
                                    </button>
                                    <span className="general-timesheet-matter-name" title={matter.matterName}>
                                      {matter.matterName}
                                    </span>
                                    {isStandardEngineeringGrid && (() => {
                                      const badge = matterStatusBadge(matter.status);
                                      return badge ? (
                                        <StatusBadge {...badge} size="small" className="general-timesheet-matter-status" />
                                      ) : null;
                                    })()}
                                    {isMatterLockedForUser(matter.matterId) && (
                                      <span className="general-timesheet-matter-locked">Locked</span>
                                    )}
                                  </div>
                                </td>
                                {weekDates.map((d) => {
                                  const key = format(d, "yyyy-MM-dd");
                                  const dayData = matter.days[key];
                                  const hours = dayData?.hours || 0;
                                  return (
                                    <td
                                      key={key}
                                      className={`general-timesheet-col-day general-timesheet-cell general-timesheet-matter-day-cell ${isToday(d) ? 'general-timesheet-today-col' : ''}`}
                                      title={dayData?.notes?.join("\n") || "Expand to add time by category"}
                                    >
                                      <span className={hours > 0 ? "general-timesheet-cell-hours-sub" : "general-timesheet-cell-placeholder"}>
                                        {hours > 0 ? formatHoursToDisplay(hours) : ""}
                                      </span>
                                    </td>
                                  );
                                })}
                                <td className="general-timesheet-col-total general-timesheet-matter-total">
                                  {matter.rowTotal > 0 ? formatTotalHours(matter.rowTotal) : ""}
                                </td>
                              </tr>
                              {isMatterExpanded &&
                                matter.categories.map((category) => {
                                  const categoryRowKey = `${emp.employeeId}-${matter.matterId}-${category.categoryCode}`;
                                  const isRowHighlighted = hoveredCell?.rowKey === categoryRowKey;
                                  const canEdit = canEditEmployeeTime(emp.employeeId) && !isMatterLockedForUser(matter.matterId);
                                  const cb = !isCustomCategories ? costCodeBudgetsByMatter.get(matter.matterId)?.[category.categoryCode] : undefined;
                                  const codeMapLoaded = !isCustomCategories && costCodeBudgetsByMatter.has(matter.matterId);
                                  const codeBudget = cb?.budget ?? 0;
                                  const codeGroup = projectedCodeGroup(matter.matterId, category.categoryCode);
                                  const codeHasBudget = codeBudget > 0;
                                  const codePercent = codeHasBudget ? Math.round((codeGroup / codeBudget) * 100) : 0;
                                  const codeOver = codeHasBudget && codeGroup > codeBudget;
                                  const codeAtOrOver = codeHasBudget && codeGroup >= codeBudget;
                                  const isEditingThisCode = editingCodeBudget?.matterId === matter.matterId && editingCodeBudget?.code === category.categoryCode;
                                  return (
                                    <tr
                                      key={categoryRowKey}
                                      className={`general-timesheet-category-row ${isRowHighlighted ? 'row-highlighted' : ''}`}
                                    >
                                      <td className="general-timesheet-col-employee general-timesheet-category-cell">
                                        <div className="general-timesheet-category-head">
                                          <span
                                            className="general-timesheet-category-name"
                                            title={isCustomCategories ? category.categoryDescription : `${category.categoryCode} - ${category.categoryDescription}`}
                                          >
                                            {isCustomCategories
                                              ? category.categoryDescription
                                              : `${category.categoryCode} - ${category.categoryDescription}`}
                                          </span>
                                          {canEditBudget && codeMapLoaded && !isEditingThisCode && (
                                            <button
                                              type="button"
                                              className="general-timesheet-code-budget-edit-btn"
                                              onClick={(e) => { e.stopPropagation(); startEditingCodeBudget(matter.matterId, category.categoryCode); }}
                                              title="Edit cost code budget"
                                              aria-label={`Edit budget for cost code ${category.categoryCode}`}
                                            >
                                              <FontAwesomeIcon icon={faPenToSquare} />
                                            </button>
                                          )}
                                        </div>
                                        {isEditingThisCode ? (
                                          <span className="general-timesheet-code-budget-edit" onClick={(e) => e.stopPropagation()}>
                                            <input
                                              type="number"
                                              step="1"
                                              min="0"
                                              className="general-timesheet-budget-input"
                                              value={codeBudgetDraft}
                                              onChange={(e) => setCodeBudgetDraft(e.target.value)}
                                              onKeyDown={(e) => handleCodeBudgetKeyDown(e, matter.matterId, category.categoryCode)}
                                              autoFocus
                                              disabled={isSavingCodeBudget}
                                              aria-label={`Budget hours for cost code ${category.categoryCode}`}
                                            />
                                            <span className="general-timesheet-budget-unit">hrs</span>
                                            <button type="button" className="general-timesheet-budget-save" onClick={() => handleSaveCodeBudget(matter.matterId, category.categoryCode)} disabled={isSavingCodeBudget} aria-label="Save cost code budget">
                                              <FontAwesomeIcon icon={faCheck} />
                                            </button>
                                            <button type="button" className="general-timesheet-budget-cancel" onClick={() => setEditingCodeBudget(null)} disabled={isSavingCodeBudget} aria-label="Cancel">
                                              <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                          </span>
                                        ) : codeHasBudget ? (
                                          <div className="general-timesheet-code-progress" title={`${formatTotalHours(codeGroup)} of ${formatTotalHours(codeBudget)} (all users)`}>
                                            <div className="general-timesheet-code-progress-track">
                                              <div
                                                className={`general-timesheet-code-progress-fill ${codeOver ? "over" : ""}`}
                                                style={{ width: `${Math.min(100, codePercent)}%` }}
                                              />
                                            </div>
                                            <span className={`general-timesheet-code-progress-label ${codeOver ? "over" : ""}`}>
                                              {formatTotalHours(codeGroup)}/{formatTotalHours(codeBudget)} · {codePercent}%
                                            </span>
                                          </div>
                                        ) : null}
                                      </td>
                                      {weekDates.map((d, colIndex) => {
                                        const key = format(d, "yyyy-MM-dd");
                                        const dayData = category.days[key];
                                        const hours = dayData?.hours || 0;
                                        const cellInvoiced = dayData?.invoiced || false;
                                        const isEditing =
                                          editingCell?.employeeId === emp.employeeId &&
                                          editingCell?.matterId === matter.matterId &&
                                          editingCell?.categoryCode === category.categoryCode &&
                                          editingCell?.dateKey === key;
                                        const isColHighlighted = hoveredCell?.colIndex === colIndex;
                                        const isCellHighlighted = isRowHighlighted && isColHighlighted;
                                        const hasPending = pendingEdits.has(makePendingKey(emp.employeeId, matter.matterId, category.categoryCode, key));
                                        // Day lock (F2): only the permish 2/3 user's own editable cells.
                                        const dayLockedCell = dayLockAppliesToMe && canEdit && dayLocks.isDayLocked(key);
                                        const cellRequest = dayLockedCell
                                          ? weekRequests.get(requestCellKey(emp.employeeId, matter.matterId, category.categoryCode, key))
                                          : undefined;
                                        const dayLockClass = !dayLockedCell
                                          ? ""
                                          : cellRequest?.status === "pending"
                                          ? "day-locked request-pending"
                                          : cellRequest?.status === "declined"
                                          ? "day-locked request-declined"
                                          : "day-locked";
                                        const dayLockTitle = !dayLockedCell
                                          ? null
                                          : cellRequest?.status === "pending"
                                          ? `Day locked — request pending: ${formatRequestHours(cellRequest.currentHours)} → ${formatRequestHours(cellRequest.requestedHours)}`
                                          : cellRequest?.status === "declined"
                                          ? `Day locked — request declined${cellRequest.decisionNote ? `: ${cellRequest.decisionNote}` : ""}`
                                          : "Day locked — click to request time";

                                        return (
                                          <td
                                            key={key}
                                            className={`general-timesheet-col-day general-timesheet-cell general-timesheet-category-day-cell ${
                                              cellInvoiced ? "invoiced" : isEditing ? "editing" : hasPending ? "pending-edit" : canEdit ? "editable" : ""
                                            } ${codeAtOrOver ? "general-timesheet-over-cap" : ""} ${isToday(d) ? 'general-timesheet-today-col' : ''} ${isColHighlighted ? 'col-highlighted' : ''} ${isCellHighlighted ? 'cell-highlighted' : ''} ${dayLockClass}`}
                                            title={cellInvoiced ? "Already invoiced" : (dayLockTitle ?? (dayData?.notes?.join("\n") || "Click to add time"))}
                                            onMouseEnter={() => setHoveredCell({ rowKey: categoryRowKey, colIndex })}
                                            onMouseLeave={() => setHoveredCell(null)}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              lastClickedCellRef.current = e.currentTarget;
                                              if (canEdit) {
                                                handleCellClick(
                                                  emp.employeeId,
                                                  matter.matterId,
                                                  category.categoryCode,
                                                  key,
                                                  hours,
                                                  cellInvoiced
                                                );
                                              }
                                            }}
                                          >
                                            {isEditing ? (
                                              <input
                                                type="number"
                                                step="0.25"
                                                min="0"
                                                max="24"
                                                className="general-timesheet-cell-input"
                                                ref={blockNumberWheel}
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onBlur={handleCellBlur}
                                                onKeyDown={handleKeyDown}
                                                autoFocus
                                                disabled={isSaving}
                                              />
                                            ) : (
                                              <>
                                                <span className="general-timesheet-cell-hours-wrap">
                                                  <span
                                                    className={
                                                      hours > 0
                                                        ? "general-timesheet-cell-hours-category"
                                                        : "general-timesheet-cell-placeholder"
                                                    }
                                                  >
                                                    {hours > 0 ? formatHoursToDisplay(hours) : ""}
                                                  </span>
                                                  {hours > 0 && cellInvoiced && (
                                                    <FontAwesomeIcon
                                                      icon={faCircleCheck}
                                                      className="general-timesheet-invoiced-check"
                                                      title="Already invoiced"
                                                    />
                                                  )}
                                                </span>
                                                {dayLockedCell && (cellRequest?.status === "pending" ? (
                                                  <span className="tdl-cell-request">
                                                    <FontAwesomeIcon icon={faClock} /> {formatRequestHours(cellRequest.currentHours)} → {formatRequestHours(cellRequest.requestedHours)}
                                                  </span>
                                                ) : (
                                                  <FontAwesomeIcon icon={faLock} className="tdl-cell-lock" aria-label="Day locked" />
                                                ))}
                                                {!isTimesheetOnlyUser && hours > 0 && (() => {
                                                  const b = dayData?.billed || 0;
                                                  const c = dayData?.costofwork || 0;
                                                  const f = dayData?.fieldrate || 0;
                                                  if (b <= 0 && c <= 0 && f <= 0) return null;
                                                  return (
                                                    <div className="general-timesheet-cell-amounts">
                                                      {b > 0 && <span className="general-timesheet-cell-amt general-timesheet-amt-billed" title="Billed">${b.toFixed(0)}</span>}
                                                      {c > 0 && <span className="general-timesheet-cell-amt general-timesheet-amt-costofwork" title="Cost of Work">${c.toFixed(0)}</span>}
                                                      {f > 0 && <span className="general-timesheet-cell-amt general-timesheet-amt-fieldrate" title="Field Rate">${f.toFixed(0)}</span>}
                                                    </div>
                                                  );
                                                })()}
                                              </>
                                            )}
                                          </td>
                                        );
                                      })}
                                      <td className="general-timesheet-col-total general-timesheet-category-total">
                                        {category.rowTotal > 0 ? formatTotalHours(category.rowTotal) : ""}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })
              )}
              {filteredGroups.length > 0 && (
                <>
                  <tr className="general-timesheet-totals-row">
                    <td className="general-timesheet-col-employee general-timesheet-totals-label">
                      <div className="general-timesheet-summary">
                        <span className="general-timesheet-summary-label">Daily total</span>
                      </div>
                    </td>
                    {weekDates.map((d) => {
                      const key = format(d, "yyyy-MM-dd");
                      const h = displayedDailyTotals[key] || 0;
                      return (
                        <td key={key} className={`general-timesheet-col-day general-timesheet-daily-total ${isToday(d) ? 'general-timesheet-today-col' : ''}`}>
                          {formatTotalHours(h)}
                        </td>
                      );
                    })}
                    <td className="general-timesheet-col-total general-timesheet-week-total-cell">
                      <span className="general-timesheet-week-total">{formatTotalHours(displayedWeekTotal)}</span>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        )}
      </div>
      {dayLockAppliesToMe && (
        <TimeRequestPopover
          anchor={requestAnchor}
          open={requestCell !== null}
          onClose={closeTimeRequest}
          cell={requestCell}
          existing={
            requestCell
              ? weekRequests.get(requestCellKey(requestCell.employeeId, requestCell.matterId, requestCell.costCode, requestCell.dateKey))
              : null
          }
          onChanged={() => void invalidateTimeRequests()}
          onStale={dayLocks.refresh}
        />
      )}
      {isDayLockAdmin && (
        <TimeRequestsPanel
          open={isRequestsPanelOpen}
          onClose={() => setIsRequestsPanelOpen(false)}
          requests={pendingRequests.requests}
          highlightRequestId={highlightRequestId}
          onApproved={handleRequestApproved}
          onChanged={() => void invalidateTimeRequests()}
          onJumpToRequest={showRequestInGrid}
          onStale={dayLocks.refresh}
        />
      )}
      {isDayLockAdmin && (
        <LockedDaysPanel
          open={isLockedDaysPanelOpen}
          onClose={() => setIsLockedDaysPanelOpen(false)}
          lockedWeekdays={dayLocks.lockedWeekdays}
          lockedDates={dayLocks.lockedDates}
          onUnlockWeekday={(weekday) => toggleWeekdayLock(weekday, false)}
          onDeleteDateLock={unlockDate}
          onApplyPreset={dayLocks.applyPreset}
          onAddDateLock={lockDate}
        />
      )}
    </div>
  );
};

export default GeneralTimesheetView;
